<script>
	import { onMount } from 'svelte';
	import { sendWithRetry } from './telegram.js';

	// Generate unique ticket ID (timestamp-based, incremental)
	const generateTicketId = () => {
		const now = new Date();
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		const hours = String(now.getHours()).padStart(2, '0');
		const minutes = String(now.getMinutes()).padStart(2, '0');
		const seconds = String(now.getSeconds()).padStart(2, '0');
		const ms = String(now.getMilliseconds()).padStart(3, '0');
		return `${year}${month}${day}-${hours}${minutes}${seconds}${ms}`;
	};

	let mobileMenuOpen = false;
	let phone = '';
	let showQuoteForm = false;
	let quoteStep = 0; // 0: initial, 1+: questions
	let isScrolled = false;
	let showBackToTop = false;
	let currentTestimonialIndex = 0;

	// Defense features slider state
	let currentDefenseSlide = 0;
	let defenseSliderInterval = null;

	// Custom confirmation popup
	let showConfirmation = false;
	let confirmationMessage = '';
	let confirmationCallback = null;
	let confirmationType = 'success'; // 'success' or 'error'

	// Form submission state
	let isSubmitting = false;
	let submitError = null;

	// Defense features data
	const defenseFeatures = [
		{
			id: 'base',
			title: 'Maior e mais tecnológica central da região',
			subtitle: 'Central de Monitoramento',
			description: 'Tecnologia de ponta com comunicação redundante. Sistema anti-sabotagem integrado. Monitoramento 24 horas por profissionais especializados.',
			image: 'products/panther-lp-base.png',
			fullWidth: true
		},
		{
			id: 'app',
			title: 'A evolução em gerenciamento de alarmes',
			subtitle: 'Aplicativo Panther',
			description: 'Acesso exclusivo para clientes Panther. Acompanhe todas as atividades do seu sistema de segurança. Disponível para celular e tablet.',
			image: 'products/panther-lp-app.png',
			fullWidth: false
		},
		{
			id: 'equipamentos',
			title: 'Equipamentos de alta tecnologia',
			subtitle: 'Tecnologia de Ponta',
			description: 'Câmeras Térmicas. Câmeras com Inteligência Artificial. Câmeras de leitura de placa (LPR). Câmeras Speed Dome com Leitor Facial. Sensores de presença sem fio.',
			image: 'products/panther-lp-ai-detection.png',
			fullWidth: true
		},
		{
			id: 'auditoria',
			title: 'Fiscalização exclusiva durante a madrugada',
			subtitle: 'Auditoria Online',
			description: 'Sistema exclusivo de fiscalização online realizado todos os dias. Garantia de que porteiros e vigilantes permaneçam ativos. Segurança reforçada nos horários mais vulneráveis.',
			image: 'products/panther-lp-portaria_virtual.jpg',
			fullWidth: true
		},
		{
			id: 'viatura',
			title: 'Pronta resposta funcional e estratégica',
			subtitle: 'Viaturas Panther',
			description: 'Frota própria com mais de 60 veículos somente para Jacareí e SJC destinados à pronta resposta operacional e técnica. Viaturas blindadas para apoio ao cliente em caso de emergência.',
			image: 'products/panther-lp-viatura.png',
			fullWidth: true
		},
		{
			id: 'equipe',
			title: 'Equipe altamente qualificada',
			subtitle: 'Nossa Equipe',
			description: '+400 colaboradores altamente capacitados. Atendentes treinados para ocorrências. Departamento técnico 24h. Vigilantes certificados pela Polícia Federal.',
			image: 'products/panther-lp-nossa-equipe.png',
			fullWidth: true
		}
	];

	// Decision tree state
	let answers = {};
	let currentNode = null;
	let progressMessage = '';
	let navigationHistory = []; // Track navigation path
	let selectedServices = []; // Track multi-select selections
	let pendingFollowUps = []; // Queue of follow-up questions

	// Form data
	let clientName = '';
	let companyName = '';
	let representanteName = '';
	let otherSource = '';
	let securityCompany = '';
	let email = '';
	let whatsappPhone = '';

	// Decision tree structure - flexible and extensible
	const decisionTree = {
		start: {
			question: "Olá, qual é seu nome?",
			isForm: true,
			formType: 'name',
			next: 'how_found',
			progressMsg: 'Prazer em conhecê-lo!'
		},
		how_found: {
			question: "Como conheceu a Panther?",
			options: [
				{
					id: 'indicacao',
					label: 'Indicação',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
					description: 'Indicação de amigos/família',
					next: 'property_type',
					progressMsg: 'Obrigado pela confiança!'
				},
				{
					id: 'google',
					label: 'Google/Internet',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
					description: 'Busca no Google',
					next: 'property_type',
					progressMsg: 'Ótimo! Estamos online para você.'
				},
				{
					id: 'social',
					label: 'Redes Sociais',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>',
					description: 'Facebook, Instagram, etc.',
					next: 'property_type',
					progressMsg: 'Siga-nos nas redes sociais!'
				},
				{
					id: 'visita',
					label: 'Visita Comercial',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
					description: 'Visita de representante',
					next: 'property_type',
					progressMsg: 'Excelente! Vamos continuar.'
				},
				{
					id: 'panfleto',
					label: 'Panfleto',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
					description: 'Panfleto ou material impresso',
					next: 'property_type',
					progressMsg: 'Obrigado pela informação!'
				},
				{
					id: 'outro',
					label: 'Outro',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Especificar',
					next: 'other_source',
					progressMsg: 'Conte-nos mais!'
				}
			]
		},
		other_source: {
			question: "Por favor, especifique como conheceu a Panther:",
			isForm: true,
			formType: 'other',
			next: 'property_type',
			progressMsg: 'Obrigado pela informação!'
		},
		property_type: {
			question: "Para qual tipo de propriedade você precisa de segurança?",
			options: [
				{
					id: 'condominio',
					label: 'Condomínio',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
					description: 'Segurança para condomínios residenciais',
					next: 'condominio_message',
					progressMsg: 'Ótimo! Vamos personalizar a solução para seu condomínio.'
				},
				{
					id: 'industria',
					label: 'Indústria',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="9" width="4" height="13"/><rect x="10" y="5" width="4" height="17"/><rect x="18" y="2" width="4" height="20"/></svg>',
					description: 'Proteção industrial completa',
					next: 'industria_message',
					progressMsg: 'Perfeito! Vamos criar uma solução industrial sob medida.'
				},
				{
					id: 'residencia',
					label: 'Residência',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><line x1="9" y1="22" x2="9" y2="12"/><line x1="15" y1="22" x2="15" y2="12"/></svg>',
					description: 'Proteção para sua casa e família',
					next: 'residencia_representante',
					progressMsg: 'Excelente! Vamos proteger sua família com a melhor tecnologia.'
				},
				{
					id: 'empresa',
					label: 'Empresa',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>',
					description: 'Segurança para seu negócio',
					next: 'empresa_name',
					progressMsg: 'Ótima escolha! Vamos garantir a segurança do seu negócio.'
				}
			]
		},
		condominio_message: {
			question: "Atendimento Personalizado para Condomínios",
			message: "Por se tratar de um condomínio, oferecemos um atendimento mais personalizado com visita técnica e proposta sob medida. Vamos coletar algumas informações para nosso especialista entrar em contato.",
			isMessage: true,
			next: 'condominio_name',
			progressMsg: 'Preparando atendimento exclusivo...'
		},
		condominio_name: {
			question: "Qual o nome do condomínio?",
			isForm: true,
			formType: 'company',
			next: 'condominio_representante',
			progressMsg: 'Registrando informações...'
		},
		condominio_representante: {
			question: "Nome do síndico ou responsável legal",
			isForm: true,
			formType: 'representante',
			next: 'condominio_services',
			progressMsg: 'Ótimo! Vamos conhecer mais sobre o condomínio...'
		},
		condominio_services: {
			question: "Quais serviços você deseja?",
			isMultiSelect: true,
			defaultNext: 'condominio_email',
			progressMsg: 'Ótimo! Vamos detalhar os serviços...',
			options: [
				{
					id: 'vigilancia',
					label: 'Vigilância',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
					description: 'Vigilância patrimonial 24h'
				},
				{
					id: 'portaria',
					label: 'Portaria',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
					description: 'Controle de acesso',
					triggersFollowUp: 'condominio_portaria_type_followup'
				},
				{
					id: 'alarme',
					label: 'Sistema de Alarme',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>',
					description: 'Alarme monitorado 24h'
				},
				{
					id: 'cameras',
					label: 'Câmeras',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
					description: 'CFTV e videomonitoramento',
					triggersFollowUp: 'condominio_cameras_ai_followup'
				},
				{
					id: 'cerca',
					label: 'Cerca Elétrica',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
					description: 'Proteção perimetral'
				}
			]
		},
		condominio_cameras_ai_followup: {
			question: "As câmeras atuais têm inteligência artificial (IA)?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Com IA',
					next: '__continue_followups__',
					progressMsg: 'Tecnologia avançada!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem IA',
					next: '__continue_followups__',
					progressMsg: 'Vamos apresentar câmeras com IA!'
				},
				{
					id: 'no_cameras',
					label: 'Ainda não tenho',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><line x1="2" y1="2" x2="22" y2="22"/></svg>',
					description: 'Quero instalar',
					next: '__continue_followups__',
					progressMsg: 'Incluiremos na proposta!'
				}
			]
		},
		condominio_portaria_type_followup: {
			question: "Qual tipo de portaria?",
			options: [
				{
					id: 'humana',
					label: 'Humana',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
					description: 'Porteiro presente',
					next: '__continue_followups__',
					progressMsg: 'Portaria tradicional!'
				},
				{
					id: 'virtual',
					label: 'Virtual',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
					description: 'Portaria remota',
					next: '__continue_followups__',
					progressMsg: 'Solução moderna!'
				},
				{
					id: 'eletronica',
					label: 'Eletrônica',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>',
					description: 'Automatizada',
					next: '__continue_followups__',
					progressMsg: 'Alta tecnologia!'
				},
				{
					id: 'hibrida',
					label: 'Híbrida',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Mista',
					next: '__continue_followups__',
					progressMsg: 'Melhor dos dois mundos!'
				}
			]
		},
		condominio_alarm: {
			question: "O condomínio já possui sistema de alarme?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
					description: 'Já possui alarme',
					next: 'condominio_cameras',
					progressMsg: 'Registrado! Podemos integrar ou atualizar.'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possui',
					next: 'condominio_cameras',
					progressMsg: 'Incluiremos na proposta!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'condominio_cameras',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		condominio_cameras: {
			question: "Possui câmeras instaladas?",
			options: [
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possuo',
					next: 'condominio_portaria',
					progressMsg: 'Vamos incluir câmeras modernas!'
				},
				{
					id: 'no_interest',
					label: 'Não tenho interesse',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
					description: 'Sem interesse agora',
					next: 'condominio_portaria',
					progressMsg: 'Sem problema!'
				},
				{
					id: 'yes_satisfied',
					label: 'Sim, estou satisfeito',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
					description: 'Satisfeito com atual',
					next: 'condominio_portaria',
					progressMsg: 'Ótimo! Sistema funcionando bem.'
				},
				{
					id: 'yes_improve',
					label: 'Sim, mas quero melhorar',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><path d="M12 9v8"/><path d="m8 13 4 4 4-4"/></svg>',
					description: 'Quero atualizar',
					next: 'condominio_cameras_ai',
					progressMsg: 'Vamos mostrar as novidades!'
				}
			]
		},
		condominio_cameras_ai: {
			question: "As câmeras atuais têm inteligência artificial (IA)?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Com IA',
					next: 'condominio_portaria',
					progressMsg: 'Tecnologia avançada!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem IA',
					next: 'condominio_portaria',
					progressMsg: 'Vamos apresentar câmeras com IA!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'condominio_portaria',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		condominio_portaria: {
			question: "O condomínio possui portaria?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
					description: 'Possui portaria',
					next: 'condominio_portaria_type',
					progressMsg: 'Vamos saber mais detalhes...'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possui',
					next: 'condominio_vigilancia',
					progressMsg: 'Podemos incluir soluções alternativas!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'condominio_vigilancia',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		condominio_portaria_type: {
			question: "Qual o tipo de portaria?",
			options: [
				{
					id: 'humana',
					label: 'Humana',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
					description: 'Porteiro presente',
					next: 'condominio_vigilancia',
					progressMsg: 'Portaria humana registrada!'
				},
				{
					id: 'virtual',
					label: 'Virtual',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
					description: 'Portaria remota',
					next: 'condominio_vigilancia',
					progressMsg: 'Portaria virtual!'
				},
				{
					id: 'eletronica',
					label: 'Eletrônica',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>',
					description: 'Totalmente automatizada',
					next: 'condominio_vigilancia',
					progressMsg: 'Portaria eletrônica!'
				},
				{
					id: 'hibrida',
					label: 'Híbrida',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Mista (humana + tecnologia)',
					next: 'condominio_vigilancia',
					progressMsg: 'Portaria híbrida!'
				}
			]
		},
		condominio_vigilancia: {
			question: "Possui serviço de vigilância?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
					description: 'Possui vigilância',
					next: 'condominio_email',
					progressMsg: 'Segurança completa!'
				},
				{
					id: 'no_but_interested',
					label: 'Não, mas tenho interesse',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>',
					description: 'Interessado no serviço',
					next: 'condominio_email',
					progressMsg: 'Vamos incluir na proposta!'
				},
				{
					id: 'no_not_interested',
					label: 'Não e não tenho interesse',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem interesse',
					next: 'condominio_email',
					progressMsg: 'Sem problema!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'condominio_email',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		condominio_email: {
			question: "Qual o e-mail para contato?",
			isForm: true,
			formType: 'email',
			next: 'final',
			progressMsg: 'Cadastro finalizado!'
		},
		industria_message: {
			question: "Atendimento Personalizado para Indústrias",
			message: "Por se tratar de uma indústria, oferecemos um atendimento mais personalizado com avaliação técnica completa e proposta customizada. Vamos coletar algumas informações para nosso especialista industrial entrar em contato.",
			isMessage: true,
			next: 'industria_name',
			progressMsg: 'Preparando atendimento exclusivo...'
		},
		industria_name: {
			question: "Qual o nome da empresa?",
			isForm: true,
			formType: 'company',
			next: 'industria_representante',
			progressMsg: 'Registrando informações...'
		},
		industria_representante: {
			question: "Nome do proprietário ou responsável legal",
			isForm: true,
			formType: 'representante',
			next: 'industria_services',
			progressMsg: 'Ótimo! Vamos conhecer mais sobre a empresa...'
		},
		industria_services: {
			question: "Quais serviços você deseja?",
			isMultiSelect: true,
			defaultNext: 'industria_email',
			progressMsg: 'Ótimo! Vamos detalhar os serviços...',
			options: [
				{
					id: 'vigilancia',
					label: 'Vigilância',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
					description: 'Vigilância patrimonial 24h'
				},
				{
					id: 'portaria',
					label: 'Portaria',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
					description: 'Controle de acesso',
					triggersFollowUp: 'industria_portaria_type_followup'
				},
				{
					id: 'alarme',
					label: 'Sistema de Alarme',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>',
					description: 'Alarme monitorado 24h'
				},
				{
					id: 'cameras',
					label: 'Câmeras',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
					description: 'CFTV e videomonitoramento',
					triggersFollowUp: 'industria_cameras_ai_followup'
				},
				{
					id: 'cerca',
					label: 'Cerca Elétrica',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
					description: 'Proteção perimetral'
				}
			]
		},
		industria_cameras_ai_followup: {
			question: "As câmeras atuais têm inteligência artificial (IA)?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Com IA',
					next: '__continue_followups__',
					progressMsg: 'Tecnologia avançada!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem IA',
					next: '__continue_followups__',
					progressMsg: 'Vamos apresentar câmeras com IA!'
				},
				{
					id: 'no_cameras',
					label: 'Ainda não tenho',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><line x1="2" y1="2" x2="22" y2="22"/></svg>',
					description: 'Quero instalar',
					next: '__continue_followups__',
					progressMsg: 'Incluiremos na proposta!'
				}
			]
		},
		industria_portaria_type_followup: {
			question: "Qual tipo de portaria?",
			options: [
				{
					id: 'humana',
					label: 'Humana',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
					description: 'Porteiro presente',
					next: '__continue_followups__',
					progressMsg: 'Portaria tradicional!'
				},
				{
					id: 'virtual',
					label: 'Virtual',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
					description: 'Portaria remota',
					next: '__continue_followups__',
					progressMsg: 'Solução moderna!'
				},
				{
					id: 'eletronica',
					label: 'Eletrônica',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>',
					description: 'Automatizada',
					next: '__continue_followups__',
					progressMsg: 'Alta tecnologia!'
				},
				{
					id: 'hibrida',
					label: 'Híbrida',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Mista',
					next: '__continue_followups__',
					progressMsg: 'Melhor dos dois mundos!'
				}
			]
		},
		industria_alarm: {
			question: "A empresa já possui sistema de alarme?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
					description: 'Já possui alarme',
					next: 'industria_cameras',
					progressMsg: 'Registrado! Podemos integrar ou atualizar.'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possui',
					next: 'industria_cameras',
					progressMsg: 'Incluiremos na proposta!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'industria_cameras',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		industria_cameras: {
			question: "Possui câmeras instaladas?",
			options: [
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possuo',
					next: 'industria_email',
					progressMsg: 'Vamos incluir câmeras modernas!'
				},
				{
					id: 'no_interest',
					label: 'Não tenho interesse',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
					description: 'Sem interesse agora',
					next: 'industria_email',
					progressMsg: 'Sem problema!'
				},
				{
					id: 'yes_satisfied',
					label: 'Sim, estou satisfeito',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
					description: 'Satisfeito com atual',
					next: 'industria_email',
					progressMsg: 'Ótimo! Sistema funcionando bem.'
				},
				{
					id: 'yes_improve',
					label: 'Sim, mas quero melhorar',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><path d="M12 9v8"/><path d="m8 13 4 4 4-4"/></svg>',
					description: 'Quero atualizar',
					next: 'industria_cameras_ai',
					progressMsg: 'Vamos mostrar as novidades!'
				}
			]
		},
		industria_cameras_ai: {
			question: "As câmeras atuais têm inteligência artificial (IA)?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Com IA',
					next: 'industria_email',
					progressMsg: 'Tecnologia avançada!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem IA',
					next: 'industria_email',
					progressMsg: 'Vamos apresentar câmeras com IA!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'industria_email',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		industria_email: {
			question: "Qual o e-mail para contato?",
			isForm: true,
			formType: 'email',
			next: 'final',
			progressMsg: 'Cadastro finalizado!'
		},
		residencia_representante: {
			question: "Nome do proprietário ou responsável legal",
			isForm: true,
			formType: 'representante',
			next: 'residencia_alarm',
			progressMsg: 'Ótimo! Vamos conhecer mais sobre sua residência...'
		},
		residencia_alarm: {
			question: "A residência possui sistema de alarme?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
					description: 'Já possui alarme',
					next: 'residencia_cameras',
					progressMsg: 'Registrado! Podemos integrar ou atualizar.'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possui',
					next: 'residencia_cameras',
					progressMsg: 'Incluiremos na proposta!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'residencia_cameras',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		residencia_cameras: {
			question: "Possui câmeras instaladas?",
			options: [
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possuo',
					next: 'residencia_crime',
					progressMsg: 'Vamos incluir câmeras modernas!'
				},
				{
					id: 'no_interest',
					label: 'Não tenho interesse',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
					description: 'Sem interesse agora',
					next: 'residencia_crime',
					progressMsg: 'Sem problema!'
				},
				{
					id: 'yes_satisfied',
					label: 'Sim, estou satisfeito',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
					description: 'Satisfeito com atual',
					next: 'residencia_crime',
					progressMsg: 'Ótimo! Sistema funcionando bem.'
				},
				{
					id: 'yes_improve',
					label: 'Sim, mas quero melhorar',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><path d="M12 9v8"/><path d="m8 13 4 4 4-4"/></svg>',
					description: 'Quero atualizar',
					next: 'residencia_cameras_ai',
					progressMsg: 'Vamos mostrar as novidades!'
				}
			]
		},
		residencia_cameras_ai: {
			question: "As câmeras atuais têm inteligência artificial (IA)?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Com IA',
					next: 'residencia_crime',
					progressMsg: 'Tecnologia avançada!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem IA',
					next: 'residencia_crime',
					progressMsg: 'Vamos apresentar câmeras com IA!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'residencia_crime',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		residencia_crime: {
			question: "Houve algum furto ou roubo na região recentemente?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Sim, houve',
					next: 'residencia_email',
					progressMsg: 'Vamos reforçar sua segurança!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
					description: 'Não houve',
					next: 'residencia_email',
					progressMsg: 'Vamos manter assim, seguro!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'residencia_email',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		residencia_email: {
			question: "Qual o e-mail para contato?",
			isForm: true,
			formType: 'email',
			next: 'final',
			progressMsg: 'Cadastro finalizado!'
		},
		empresa_environment: {
			question: "Qual o tipo de ambiente da empresa?",
			options: [
				{
					id: 'office',
					label: 'Escritório',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
					description: 'Ambiente corporativo',
					next: 'empresa_name',
					progressMsg: 'Segurança corporativa inteligente!'
				},
				{
					id: 'retail',
					label: 'Loja/Comércio',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>',
					description: 'Varejo e atendimento',
					next: 'empresa_name',
					progressMsg: 'Proteção para seu comércio!'
				},
				{
					id: 'mixed',
					label: 'Misto',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>',
					description: 'Escritório + operação',
					next: 'empresa_name',
					progressMsg: 'Solução completa para seu negócio!'
				}
			]
		},
		empresa_name: {
			question: "Qual o nome da empresa?",
			isForm: true,
			formType: 'company',
			next: 'empresa_representante',
			progressMsg: 'Registrando informações...'
		},
		empresa_representante: {
			question: "Nome do proprietário ou responsável legal",
			isForm: true,
			formType: 'representante',
			next: 'empresa_alarm',
			progressMsg: 'Ótimo! Vamos conhecer mais sobre sua empresa...'
		},
		empresa_alarm: {
			question: "A empresa possui sistema de alarme?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
					description: 'Já possui alarme',
					next: 'empresa_cameras',
					progressMsg: 'Registrado! Podemos integrar ou atualizar.'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possui',
					next: 'empresa_cameras',
					progressMsg: 'Incluiremos na proposta!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'empresa_cameras',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		empresa_cameras: {
			question: "Possui câmeras instaladas?",
			options: [
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Não possuo',
					next: 'empresa_email',
					progressMsg: 'Vamos incluir câmeras modernas!'
				},
				{
					id: 'no_interest',
					label: 'Não tenho interesse',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
					description: 'Sem interesse agora',
					next: 'empresa_email',
					progressMsg: 'Sem problema!'
				},
				{
					id: 'yes_satisfied',
					label: 'Sim, estou satisfeito',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
					description: 'Satisfeito com atual',
					next: 'empresa_email',
					progressMsg: 'Ótimo! Sistema funcionando bem.'
				},
				{
					id: 'yes_improve',
					label: 'Sim, mas quero melhorar',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><path d="M12 9v8"/><path d="m8 13 4 4 4-4"/></svg>',
					description: 'Quero atualizar',
					next: 'empresa_cameras_ai',
					progressMsg: 'Vamos mostrar as novidades!'
				}
			]
		},
		empresa_cameras_ai: {
			question: "As câmeras atuais têm inteligência artificial (IA)?",
			options: [
				{
					id: 'yes',
					label: 'Sim',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48 2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48 2.83-2.83"/></svg>',
					description: 'Com IA',
					next: 'empresa_email',
					progressMsg: 'Tecnologia avançada!'
				},
				{
					id: 'no',
					label: 'Não',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
					description: 'Sem IA',
					next: 'empresa_email',
					progressMsg: 'Vamos apresentar câmeras com IA!'
				},
				{
					id: 'prefer_not_answer',
					label: 'Não sei, ou prefiro não responder',
					icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
					description: 'Prefiro não responder',
					next: 'empresa_email',
					progressMsg: 'Sem problema, vamos continuar!'
				}
			]
		},
		empresa_email: {
			question: "Qual o e-mail para contato?",
			isForm: true,
			formType: 'email',
			next: 'final',
			progressMsg: 'Cadastro finalizado!'
		},
		final: {
			question: null, // This is the end - show contact form
			isFinal: true
		}
	};

	// Calculate progress percentage
	$: progressPercentage = calculateProgress();

	function calculateProgress() {
		if (quoteStep === 0) return 0;
		const totalAnswers = Object.keys(answers).length;
		// Estimate total steps needed (will vary by path, but good enough)
		const estimatedTotal = 5;
		return Math.min((totalAnswers / estimatedTotal) * 100, 90);
	}

	function startQuote() {
		quoteStep = 1;
		currentNode = decisionTree.start;
		answers = {};
		progressMessage = '';
		navigationHistory = ['start'];
		// Scroll to hero section
		const heroSection = document.querySelector('.hero');
		if (heroSection) {
			heroSection.scrollIntoView({ behavior: 'smooth' });
		}
	}

	let progressTimeout = null;

	function selectOption(option) {
		// Clear any existing timeout
		if (progressTimeout) {
			clearTimeout(progressTimeout);
		}

		// Store answer with the current node's key
		const currentNodeKey = Object.keys(decisionTree).find(k => decisionTree[k] === currentNode);
		if (currentNodeKey) {
			answers[currentNodeKey] = option.id;
		}

		// Show progress message
		progressMessage = option.progressMsg;

		// Determine next node - handle follow-up continuation
		let nextNodeId = option.next;

		if (nextNodeId === '__continue_followups__') {
			if (pendingFollowUps.length > 0) {
				nextNodeId = pendingFollowUps.shift();
				pendingFollowUps = pendingFollowUps; // Trigger reactivity
			} else {
				// Find the multi-select node to get defaultNext
				const multiSelectNodeKey = navigationHistory.find(h => decisionTree[h]?.isMultiSelect);
				nextNodeId = multiSelectNodeKey ? decisionTree[multiSelectNodeKey].defaultNext : 'final';
			}
		}

		// Move to next node immediately
		if (nextNodeId === 'final') {
			currentNode = decisionTree.final;
			navigationHistory.push('final');
		} else {
			currentNode = decisionTree[nextNodeId];
			navigationHistory.push(nextNodeId);
		}
		quoteStep++;

		// Clear progress message after 3 seconds
		progressTimeout = setTimeout(() => {
			progressMessage = '';
			progressTimeout = null;
		}, 3000);
	}

	// Multi-select toggle function
	function handleMultiSelectToggle(optionId) {
		if (selectedServices.includes(optionId)) {
			selectedServices = selectedServices.filter(id => id !== optionId);
		} else {
			selectedServices = [...selectedServices, optionId];
		}
	}

	// Continue from multi-select - builds follow-up queue
	function continueFromMultiSelect() {
		if (selectedServices.length === 0) {
			showCustomConfirmation('Por favor, selecione pelo menos um serviço.', null, 'error');
			return;
		}

		// Clear any existing timeout
		if (progressTimeout) {
			clearTimeout(progressTimeout);
		}

		// Store selections
		const nodeKey = Object.keys(decisionTree).find(k => decisionTree[k] === currentNode);
		answers[nodeKey] = selectedServices.join(',');

		// Build follow-up queue based on selections (order matches options array)
		pendingFollowUps = [];
		if (currentNode.options) {
			for (const opt of currentNode.options) {
				if (selectedServices.includes(opt.id) && opt.triggersFollowUp) {
					pendingFollowUps.push(opt.triggersFollowUp);
				}
			}
		}

		// Show progress message
		progressMessage = currentNode.progressMsg;

		// Navigate to first follow-up or default
		let nextNodeId;
		if (pendingFollowUps.length > 0) {
			nextNodeId = pendingFollowUps.shift();
			pendingFollowUps = pendingFollowUps; // Trigger reactivity
		} else {
			nextNodeId = currentNode.defaultNext;
		}

		currentNode = decisionTree[nextNodeId];
		navigationHistory.push(nextNodeId);
		quoteStep++;
		selectedServices = []; // Reset for future use

		// Clear progress message after 3 seconds
		progressTimeout = setTimeout(() => {
			progressMessage = '';
			progressTimeout = null;
		}, 3000);
	}

	function continueFromMessage() {
		if (currentNode && currentNode.isMessage && currentNode.next) {
			// Clear any existing timeout
			if (progressTimeout) {
				clearTimeout(progressTimeout);
			}

			// Show progress message
			progressMessage = currentNode.progressMsg;

			// Move to next node
			if (currentNode.next === 'final') {
				currentNode = decisionTree.final;
			} else {
				currentNode = decisionTree[currentNode.next];
			}
			quoteStep++;

			// Clear progress message after 3 seconds
			progressTimeout = setTimeout(() => {
				progressMessage = '';
				progressTimeout = null;
			}, 3000);
		}
	}

	function continueFromForm() {
		if (currentNode && currentNode.isForm) {
			// Validate form based on formType
			let isValid = false;
			let errorMessage = '';

			if (currentNode.formType === 'name') {
				const name = clientName.trim();
				// Check minimum length
				if (name.length < 3) {
					errorMessage = 'Por favor, insira um nome com pelo menos 3 caracteres.';
				}
				// Check for numbers
				else if (/\d/.test(name)) {
					errorMessage = 'O nome não pode conter números.';
				}
				// Check for dots
				else if (name.includes('.')) {
					errorMessage = 'O nome não pode conter pontos.';
				}
				// Check if it's only letters and spaces
				else if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(name)) {
					errorMessage = 'Por favor, insira um nome válido (apenas letras).';
				}
				else {
					isValid = true;
				}
			} else if (currentNode.formType === 'company') {
				isValid = companyName.trim().length > 0;
			} else if (currentNode.formType === 'representante') {
				isValid = representanteName.trim().length > 0;
			} else if (currentNode.formType === 'other') {
				isValid = otherSource.trim().length > 0;
			} else if (currentNode.formType === 'phone') {
				isValid = whatsappPhone.trim().length >= 14; // (12) 99999-9999
			} else if (currentNode.formType === 'email') {
				isValid = email.trim().length > 0 && email.includes('@');
			}

			if (!isValid) {
				if (errorMessage) {
					showCustomConfirmation(errorMessage, null, 'error');
				} else if (currentNode.formType === 'email') {
					showCustomConfirmation('Por favor, insira um e-mail válido.', null, 'error');
				} else if (currentNode.formType === 'phone') {
					showCustomConfirmation('Por favor, insira um telefone válido.', null, 'error');
				} else {
					showCustomConfirmation('Por favor, preencha o campo antes de continuar.', null, 'error');
				}
				return;
			}

			// Clear any existing timeout
			if (progressTimeout) {
				clearTimeout(progressTimeout);
			}

			// Show progress message
			progressMessage = currentNode.progressMsg;

			// Move to next node
			if (currentNode.next === 'final') {
				currentNode = decisionTree.final;
				navigationHistory.push('final');
			} else {
				const nextNode = currentNode.next;
				currentNode = decisionTree[nextNode];
				navigationHistory.push(nextNode);
			}
			quoteStep++;

			// Clear progress message after 3 seconds
			progressTimeout = setTimeout(() => {
				progressMessage = '';
				progressTimeout = null;
			}, 3000);
		}
	}

	function goBack() {
		// Clear progress message
		if (progressTimeout) {
			clearTimeout(progressTimeout);
			progressTimeout = null;
		}
		progressMessage = '';

		if (navigationHistory.length <= 1) {
			// We're at the start, go back to the initial screen
			quoteStep = 0;
			currentNode = null;
			answers = {};
			navigationHistory = [];
			return;
		}

		// Remove current node from history
		navigationHistory.pop();

		// Get previous node
		const previousNodeKey = navigationHistory[navigationHistory.length - 1];

		// Navigate to previous node
		if (previousNodeKey === 'final') {
			currentNode = decisionTree.final;
		} else if (previousNodeKey === 'start') {
			currentNode = decisionTree.start;
		} else {
			currentNode = decisionTree[previousNodeKey];
		}

		// Decrease quote step
		quoteStep--;

		// Remove last answer if applicable
		const answerKeys = Object.keys(answers);
		if (answerKeys.length > 0) {
			delete answers[answerKeys[answerKeys.length - 1]];
		}
	}

	function formatPhoneNumber(value) {
		// Remove all non-digits
		const digits = value.replace(/\D/g, '');

		// Format: (12) 99999-9999
		if (digits.length === 0) return '';
		if (digits.length <= 2) return digits;
		if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
		return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
	}

	function handlePhoneInput(event) {
		const formatted = formatPhoneNumber(event.target.value);

		// Update the appropriate variable based on context
		if (currentNode && currentNode.formType === 'phone') {
			whatsappPhone = formatted;
		} else {
			phone = formatted;
		}
	}

	const toggleMobileMenu = () => {
		mobileMenuOpen = !mobileMenuOpen;
	};

	const showCustomConfirmation = (message, callback = null, type = 'success') => {
		confirmationMessage = message;
		confirmationCallback = callback;
		confirmationType = type;
		showConfirmation = true;
	};

	const closeConfirmation = () => {
		showConfirmation = false;
		confirmationMessage = '';
		confirmationType = 'success';

		// Execute callback if exists
		if (confirmationCallback) {
			confirmationCallback();
			confirmationCallback = null;
		}
	};

	const resetFormAndGoHome = () => {
		// Reset all form state
		quoteStep = 0;
		showQuoteForm = false;
		phone = '';
		answers = {};
		currentNode = null;
		progressMessage = '';
		navigationHistory = [];
		clientName = '';
		companyName = '';
		representanteName = '';
		otherSource = '';

		// Scroll to top
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const validatePhoneNumber = (phoneNumber) => {
		// Remove all non-digit characters for validation
		const digitsOnly = phoneNumber.replace(/\D/g, '');

		// Brazilian phone format: (DD) 9XXXX-XXXX or (DD) XXXX-XXXX
		// Should have 10 or 11 digits (2 for DDD + 8 or 9 for number)
		if (digitsOnly.length < 10 || digitsOnly.length > 11) {
			return false;
		}

		// Check if format matches (DD) NNNNN-NNNN or (DD) NNNN-NNNN
		const phoneRegex = /^\(\d{2}\)\s?\d{4,5}-?\d{4}$/;
		return phoneRegex.test(phoneNumber);
	};

	// Helper function to get business type label
	const getBusinessTypeLabel = () => {
		// Check both possible keys
		const type = answers.property_type || answers.business_type;
		if (type === 'condominio') return 'Condomínio';
		if (type === 'industria') return 'Indústria';
		if (type === 'empresa') return 'Empresa';
		if (type === 'residencia') return 'Residência';
		return type || 'Não especificado';
	};

	// Helper function to get selected services
	const getSelectedServices = () => {
		const type = answers.property_type || answers.business_type;
		const servicesKey = type === 'condominio'
			? 'condominio_services'
			: type === 'industria'
				? 'industria_services'
				: null;

		if (!servicesKey || !answers[servicesKey]) return [];

		const selected = answers[servicesKey].split(',');
		const labels = {
			'vigilancia': 'Vigilância',
			'portaria': 'Portaria',
			'alarme': 'Sistema de Alarme',
			'cameras': 'Câmeras',
			'cerca': 'Cerca Elétrica'
		};
		return selected.map(id => labels[id] || id);
	};

	// Map answer keys to readable labels
	const answerLabels = {
		'start': 'Nome do cliente',
		'how_found': 'Como nos encontrou',
		'how_found_other': 'Outra fonte',
		'property_type': 'Tipo de imóvel',
		'business_type': 'Tipo de negócio',
		// Condomínio
		'condominio_name': 'Nome do condomínio',
		'condominio_representante': 'Representante',
		'condominio_services': 'Serviços selecionados',
		'condominio_alarm': 'Sistema de alarme',
		'condominio_cameras': 'Câmeras de segurança',
		'condominio_cameras_ai': 'Câmeras com IA',
		'condominio_cameras_ai_followup': 'Câmeras com IA',
		'condominio_portaria': 'Portaria',
		'condominio_portaria_type': 'Tipo de portaria',
		'condominio_portaria_type_followup': 'Tipo de portaria',
		'condominio_vigilancia': 'Vigilância',
		'condominio_message': 'Mensagem adicional',
		'condominio_email': 'Email',
		// Indústria
		'industria_name': 'Nome da indústria',
		'industria_representante': 'Representante',
		'industria_services': 'Serviços selecionados',
		'industria_cameras_ai_followup': 'Câmeras com IA',
		'industria_portaria_type': 'Tipo de portaria',
		'industria_employees': 'Funcionários',
		'industria_access_hours': 'Horário de acesso',
		'industria_email': 'Email',
		// Empresa
		'empresa_name': 'Nome da empresa',
		'empresa_size': 'Tamanho da empresa',
		'empresa_location': 'Localização',
		'empresa_security': 'Segurança atual',
		'empresa_priority': 'Prioridade',
		'empresa_email': 'Email',
		// Residência
		'residencia_type': 'Tipo de residência',
		'residencia_size': 'Tamanho',
		'residencia_security': 'Segurança atual',
		'residencia_email': 'Email',
		'security_company': 'Empresa de segurança atual',
		'residenciaalarm': 'Sistema de alarme residencial',
		'residenciacameras': 'Câmeras residenciais',
		'residenciacamerasai': 'Câmeras com IA',
		'residenciacrime': 'Histórico de ocorrências',
		'residenciaaccess': 'Controle de acesso',
		'residenciafence': 'Cerca elétrica',
		'residenciamonitoring': 'Monitoramento 24h',
		// Visita
		'visita': 'Visita técnica',
		'visita_date': 'Data da visita',
		'visita_time': 'Horário da visita'
	};

	// Map answer values to readable labels
	const answerValueLabels = {
		// How found
		'google': 'Google',
		'instagram': 'Instagram',
		'indicacao': 'Indicação',
		'outdoor': 'Outdoor/Placa',
		'radio': 'Rádio',
		'visita': 'Visita técnica',
		'other': 'Outro',
		// Business type / Property type
		'condominio': 'Condomínio',
		'industria': 'Indústria',
		'empresa': 'Empresa',
		'residencia': 'Residência',
		'comercio': 'Comércio',
		// Services
		'vigilancia': 'Vigilância',
		'portaria': 'Portaria',
		'alarme': 'Sistema de Alarme',
		'cameras': 'Câmeras',
		'cerca': 'Cerca Elétrica',
		'cftv': 'CFTV',
		'monitoramento': 'Monitoramento',
		// Cameras AI
		'yes': 'Sim',
		'no': 'Não',
		'no_cameras': 'Ainda não tenho câmeras',
		'dont_know': 'Não sei / Prefiro não responder',
		'prefer_not': 'Prefiro não responder',
		// Residential alarm status
		'prefernotanswer': 'Prefiro não responder',
		'none': 'Não tenho',
		'basic': 'Sistema básico',
		'complete': 'Sistema completo',
		'toinstall': 'Pretendo instalar',
		// Residential cameras status
		'yesimprove': 'Sim, desejo melhorar',
		'yes_improve': 'Sim, desejo melhorar',
		'yeskeep': 'Sim, manter atual',
		'yessatisfied': 'Sim, estou satisfeito',
		'yes_satisfied': 'Sim, estou satisfeito',
		'noinstall': 'Não, desejo instalar',
		'nointerest': 'Não tenho interesse',
		// Crime history
		'yes_recent': 'Sim, recentemente',
		'yes_past': 'Sim, no passado',
		'no_never': 'Não',
		// Portaria type
		'humana': 'Portaria Humana',
		'virtual': 'Portaria Virtual',
		'eletronica': 'Portaria Eletrônica',
		'hibrida': 'Portaria Híbrida',
		// Employees
		'1-50': '1 a 50 funcionários',
		'51-200': '51 a 200 funcionários',
		'201-500': '201 a 500 funcionários',
		'500+': 'Mais de 500 funcionários',
		// Access hours
		'commercial': 'Horário comercial',
		'extended': 'Horário estendido',
		'24h': '24 horas',
		'shifts': 'Turnos',
		// Generic
		'true': 'Sim',
		'false': 'Não'
	};

	// Format answer value
	const formatAnswerValue = (key, value) => {
		if (!value) return '';

		// Handle comma-separated services
		if (key.includes('_services')) {
			const services = value.split(',');
			return services.map(s => answerValueLabels[s] || s).join(', ');
		}

		return answerValueLabels[value] || value;
	};

	// Format message for Telegram (Professional version with minimal emojis)
	const formatTelegramMessage = (formData) => {
		const ticketId = generateTicketId();

		let message = `*TICKET #${ticketId}*\n`;
		message += '━━━━━━━━━━━━━━━━━━━━━━\n';
		message += '*Nova Solicitação de Orçamento*\n\n';

		// Contact info section - minimal emojis
		message += '*DADOS DE CONTATO*\n';
		if (formData.clientName) message += `• Nome: ${formData.clientName}\n`;
		if (formData.companyName) message += `• Empresa: ${formData.companyName}\n`;
		if (formData.representanteName) message += `• Representante: ${formData.representanteName}\n`;
		if (formData.email) message += `• Email: ${formData.email}\n`;
		if (formData.phone) message += `• Telefone: ${formData.phone}\n`;

		// Business type
		if (formData.type && formData.type !== 'Não especificado') {
			message += `\n*TIPO DE CLIENTE*\n`;
			message += `${formData.type}\n`;
		}

		// Services section - limit to 10 services
		if (formData.services && formData.services.length > 0) {
			message += '\n*SERVIÇOS SELECIONADOS*\n';
			const servicesToShow = formData.services.slice(0, 10);
			servicesToShow.forEach(service => {
				message += `• ${service}\n`;
			});
			if (formData.services.length > 10) {
				message += `• ... e mais ${formData.services.length - 10} serviços\n`;
			}
		}

		// All other answers - limit to 8 fields
		const excludeKeys = [
			'start', // Client name already shown
			'property_type', 'business_type', // Type already shown
			'condominio_services', 'industria_services', // Services already shown
			'condominio_name', 'industria_name', 'empresa_name', // Company name already shown
			'condominio_representante', 'industria_representante', // Representative already shown
			'condominio_email', 'industria_email', 'empresa_email', 'residencia_email' // Email already shown
		];
		const additionalAnswers = Object.entries(formData.answers || {})
			.filter(([key]) => !excludeKeys.includes(key))
			.slice(0, 8); // Limit to 8 additional fields

		if (additionalAnswers.length > 0) {
			message += '\n*INFORMAÇÕES ADICIONAIS*\n';
			additionalAnswers.forEach(([key, value]) => {
				// Get label, or escape the raw key if no label found
				let label = answerLabels[key];
				if (!label) {
					// Safety fallback: escape underscores to prevent Markdown parsing errors
					label = key.replace(/_/g, '\\_');
					console.warn(`Missing label for key: ${key}`);
				}
				const formattedValue = formatAnswerValue(key, value);
				if (formattedValue) {
					// Truncate long values
					const truncated = formattedValue.length > 100
						? formattedValue.substring(0, 97) + '...'
						: formattedValue;
					message += `• ${label}: ${truncated}\n`;
				}
			});
		}

		// Timestamp
		message += '\n━━━━━━━━━━━━━━━━━━━━━━\n';
		const now = new Date();
		const formatted = now.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
		message += `${formatted}`;

		return message;
	};


	const handlePhoneSubmit = async () => {
		// Validate phone number format
		if (!phone || phone.trim() === '') {
			showCustomConfirmation('Por favor, informe seu número de telefone.', null, 'error');
			return;
		}

		if (!validatePhoneNumber(phone)) {
			showCustomConfirmation('Por favor, informe um número de telefone válido no formato (DD) NNNNN-NNNN.', null, 'error');
			return;
		}

		// Set loading state
		isSubmitting = true;
		submitError = null;

		try {
			// Prepare form data for Telegram notification
			const formData = {
				type: getBusinessTypeLabel(),
				clientName: clientName || '',
				companyName: companyName || '',
				representanteName: representanteName || '',
				email: email || '',
				phone: phone,
				services: getSelectedServices(),
				answers: answers,
				timestamp: new Date().toISOString()
			};

			console.log('Submitting form:', formData);

			// Send with 3 retries, 5 s each, 2 s gap — see src/telegram.js
			const result = await sendWithRetry(formatTelegramMessage(formData));

			if (result.success) {
				showCustomConfirmation(
					'Recebemos sua solicitação! Entraremos em contato em breve.',
					resetFormAndGoHome,
					'success'
				);
			} else {
				throw new Error(result.error || 'Falha ao enviar mensagem');
			}
		} catch (error) {
			console.error('Form submission error:', error);
			// More natural, business-friendly error message
			showCustomConfirmation(
				'Não conseguimos processar sua solicitação no momento. Por favor, tente novamente. Se o problema persistir, entre em contato pelo telefone (12) 3951-8505.',
				null, // No callback - don't reset form, let user try again
				'error'
			);
		} finally {
			isSubmitting = false;
		}
	};

	const scrollToQuote = () => {
		const heroSection = document.querySelector('.hero');
		if (heroSection) {
			heroSection.scrollIntoView({ behavior: 'smooth' });
		}
		setTimeout(() => {
			if (quoteStep === 0) {
				startQuote();
			}
		}, 600);
	};

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// Placa slider functions
	const totalPlacaSlides = 3; // Total number of slides

	const startPlacaSlider = () => {
		if (!placaSliderPaused && totalPlacaSlides > 1) {
			placaSliderInterval = setInterval(() => {
				currentPlacaSlide = (currentPlacaSlide + 1) % totalPlacaSlides;
			}, 3000); // 3 seconds
		}
	};

	const stopPlacaSlider = () => {
		if (placaSliderInterval) {
			clearInterval(placaSliderInterval);
			placaSliderInterval = null;
		}
	};

	const goToSlide = (index) => {
		currentPlacaSlide = index;
		placaSliderPaused = true;
		stopPlacaSlider();
	};

	const nextSlide = () => {
		currentPlacaSlide = (currentPlacaSlide + 1) % totalPlacaSlides;
		placaSliderPaused = true;
		stopPlacaSlider();
	};

	const prevSlide = () => {
		currentPlacaSlide = (currentPlacaSlide - 1 + totalPlacaSlides) % totalPlacaSlides;
		placaSliderPaused = true;
		stopPlacaSlider();
	};

	// Defense features slider functions
	const startDefenseSlider = () => {
		defenseSliderInterval = setInterval(() => {
			currentDefenseSlide = (currentDefenseSlide + 1) % defenseFeatures.length;
		}, 5000); // 5 seconds per slide
	};

	const stopDefenseSlider = () => {
		if (defenseSliderInterval) {
			clearInterval(defenseSliderInterval);
			defenseSliderInterval = null;
		}
	};

	const goToDefenseSlide = (index) => {
		currentDefenseSlide = index;
		stopDefenseSlider();
		startDefenseSlider();
	};

	const nextDefenseSlide = () => {
		currentDefenseSlide = (currentDefenseSlide + 1) % defenseFeatures.length;
		stopDefenseSlider();
		startDefenseSlider();
	};

	const prevDefenseSlide = () => {
		currentDefenseSlide = (currentDefenseSlide - 1 + defenseFeatures.length) % defenseFeatures.length;
		stopDefenseSlider();
		startDefenseSlider();
	};

	onMount(() => {
		const handleScroll = () => {
			// Show CTA navbar after scrolling past hero section (about 600px)
			isScrolled = window.scrollY > 600;
			// Show back to top button after scrolling 400px
			showBackToTop = window.scrollY > 400;
		};

		window.addEventListener('scroll', handleScroll);

		// Start placa slider
		startPlacaSlider();
		// Start defense features slider
		startDefenseSlider();

		return () => {
			window.removeEventListener('scroll', handleScroll);
			stopPlacaSlider();
			stopDefenseSlider();
		};
	});

	// CTA Bar configuration - reusable component data
	const ctaBars = [
		{
			id: 'cta-panther-seguranca',
			title: 'Proteja o que mais importa com Panther Segurança',
			buttonText: 'SOLICITAR ORÇAMENTO',
			discount: 'Contrate com condições especiais'
		},
		{
			id: 'cta-comparison',
			title: 'Melhor proteção contra roubo e invasão',
			buttonText: 'CALCULE',
			discount: 'Faça uma simulação gratuita'
		},
		{
			id: 'cta-trusted-companies',
			title: 'Junte-se às empresas que confiam na Panther',
			buttonText: 'FALE CONOSCO',
			discount: 'Soluções personalizadas para o seu negócio'
		}
	];

	const stats = [
		{ value: "8.000+", label: "Unidades Protegidas" },
		{ value: "24/7", label: "Monitoramento" },
		{ value: "São José dos Campos e Jacareí", label: "Atuação Regional" }
	];

	const features = [
		{
			title: "Controle Biométrico",
			description: "Reconhecimento facial e digital avançado para entrada segura e sem contato.",
			icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`
		},
		{
			title: "Monitoramento em Tempo Real",
			description: "Acompanhe todos os eventos de acesso de qualquer lugar, a qualquer momento.",
			icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>`
		},
		{
			title: "Integração Inteligente",
			description: "Conecte-se facilmente aos seus sistemas de gestão predial existentes.",
			icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>`
		},
		{
			title: "Gestão na Nuvem",
			description: "Gerencie permissões, usuários e dispositivos de uma plataforma centralizada.",
			icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path></svg>`
		}
	];

	const solutions = [
		{
			title: "Condomínios Residenciais",
			description: "Controle de acesso completo com gestão de visitantes e moradores.",
			image: "🏘️"
		},
		{
			title: "Edifícios Comerciais",
			description: "Segurança empresarial com análises avançadas e relatórios de conformidade.",
			image: "🏢"
		},
		{
			title: "Uso Misto",
			description: "Soluções flexíveis para ambientes complexos e multi-inquilinos.",
			image: "🌆"
		}
	];

	const testimonials = [
		{
			id: 1,
			name: "Ana Clara",
			age: "32 anos",
			text: "O sistema funciona muito bem, é moderno e a tecnologia é super avançada. O aplicativo que uso para acessar as informações e imagens é muito bom e fácil de usar.",
			rating: 5,
			image: "" // Add image path when available
		},
		{
			id: 2,
			name: "Marisa",
			age: "61 anos",
			text: "Por uma real tentativa de roubo, pude comprovar que o videomonitoramento é realmente muito efetivo, identificou os bandidos e foram presos logo em seguida.",
			rating: 5,
			image: "" // Add image path when available
		},
		{
			id: 3,
			name: "Roberto Silva",
			age: "45 anos",
			text: "A Panther transformou a segurança do nosso condomínio. O suporte é excepcional e o sistema nunca falha. Recomendo para todos!",
			rating: 5,
			image: "" // Add image path when available
		},
		{
			id: 4,
			name: "Patricia Santos",
			age: "38 anos",
			text: "Estou muito satisfeita com a Panther. A instalação foi rápida e o sistema é muito confiável. Me sinto muito mais segura agora.",
			rating: 5,
			image: "" // Add image path when available
		},
		{
			id: 5,
			name: "Carlos Eduardo",
			age: "52 anos",
			text: "Excelente custo-benefício! O aplicativo é intuitivo e consigo ver tudo em tempo real. A equipe de suporte também é muito atenciosa.",
			rating: 5,
			image: "" // Add image path when available
		},
		{
			id: 6,
			name: "Juliana Mendes",
			age: "29 anos",
			text: "Recomendo a Panther para todos! Sistema moderno, fácil de usar e o mais importante: funciona perfeitamente. Vale cada centavo investido.",
			rating: 5,
			image: "" // Add image path when available
		}
	];

	// Detect if mobile for testimonials
	let isMobile = false;
	if (typeof window !== 'undefined') {
		isMobile = window.innerWidth <= 480;
		window.addEventListener('resize', () => {
			isMobile = window.innerWidth <= 480;
		});
	}

	const nextTestimonial = () => {
		const itemsPerPage = isMobile ? 1 : 3;
		currentTestimonialIndex = (currentTestimonialIndex + 1) % Math.ceil(testimonials.length / itemsPerPage);
	};

	const prevTestimonial = () => {
		const itemsPerPage = isMobile ? 1 : 3;
		currentTestimonialIndex = (currentTestimonialIndex - 1 + Math.ceil(testimonials.length / itemsPerPage)) % Math.ceil(testimonials.length / itemsPerPage);
	};

	$: itemsPerPage = isMobile ? 1 : 3;
	$: visibleTestimonials = testimonials.slice(currentTestimonialIndex * itemsPerPage, currentTestimonialIndex * itemsPerPage + itemsPerPage);

	// Touch swipe support for testimonials
	let touchStartX = 0;
	let touchEndX = 0;

	const handleTouchStart = (e) => {
		touchStartX = e.changedTouches[0].screenX;
	};

	const handleTouchEnd = (e) => {
		touchEndX = e.changedTouches[0].screenX;
		handleSwipe();
	};

	const handleSwipe = () => {
		if (touchEndX < touchStartX - 50) {
			// Swipe left - next
			nextTestimonial();
		}
		if (touchEndX > touchStartX + 50) {
			// Swipe right - prev
			prevTestimonial();
		}
	};

	// Trusted companies - Add real logo paths here
	const trustedCompanies = [
		{ name: "Band", logo: "customers/cliente-logo_band.svg" },
		{ name: "BMW", logo: "customers/cliente-logo_bmw.svg" },
		{ name: "Havan", logo: "customers/cliente-logo_havan.png" },
		{ name: "Jeep", logo: "customers/cliente-logo_jeep.svg" },
		{ name: "Nikkeypar", logo: "customers/cliente-logo_nikkeypar.png" },
		{ name: "Oba Hortifruti", logo: "customers/cliente-logo_oba.png" },
		{ name: "Univap", logo: "customers/cliente-logo_univap.png" },
		{ name: "Vanguarda", logo: "customers/cliente-logo_vanguarda.png" }
	];

	const comparisonFeatures = [
		{ feature: "Equipamentos que integram a central de monitoramento", panther: true, competitors: true },
		{ feature: "Instalação do sistema sem necessidade de obras", panther: true, competitors: true },
		{ feature: "Botão de pânico instalado em local adequado para maior segurança", panther: true, competitors: true },
		{ feature: "Proposta personalizada para atender a real necessidade", panther: true, competitors: false },
		{ feature: "Soluções que se adaptam à realidade do local monitorado", panther: true, competitors: false },
		{ feature: "Sensor de infravermelho em todos os ambientes", panther: true, competitors: false },
		{ feature: "Possui Vigilantes Certificados pela Polícia Federal", panther: true, competitors: false },
		{ feature: "Bateria NoBreak com autonomia de até 48h", panther: true, competitors: false },
		{ feature: "Central de alarme protegida por sensores e caixa metálica", panther: true, competitors: false },
		{ feature: "Comunicação por Chip de Celular, Internet e Rádio", panther: true, competitors: false },
		{ feature: "Duas Sirenes de Alta Potência (240Db total)", panther: true, competitors: false }
	];
</script>

<div class="app">
	<!-- Mobile Menu Overlay -->
	{#if mobileMenuOpen}
		<div class="mobile-menu-overlay" class:active={mobileMenuOpen} on:click={toggleMobileMenu}></div>
	{/if}

	<!-- Navigation -->
	<nav class="navbar" class:scrolled={isScrolled}>
		<div class="container nav-container">
			<a href="tel:+551239518505" class="mobile-call-btn">
				<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
				</svg>
				LIGAR
			</a>

			<a href="#" class="logo">
				<img src="panther-logo.svg" alt="Panther" class="logo-img" />
			</a>

			<button class="mobile-menu-btn" class:active={mobileMenuOpen} on:click={toggleMobileMenu}>
				<span class="hamburger"></span>
				<span class="menu-text">MENU</span>
			</button>

			<ul class="nav-links" class:active={mobileMenuOpen}>
				<button class="mobile-menu-close" on:click={toggleMobileMenu} aria-label="Fechar menu">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</button>
				<li class="nav-phone-container">
					<span class="nav-phone-label">Ligue Agora</span>
					<a href="tel:+551239518505" class="nav-phone">(12) 3951-8505</a>
				</li>
				<li>
					<a href="https://panther.com.br" target="_blank" rel="noopener noreferrer" class="nav-site-link">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="site-icon">
							<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
							<polyline points="15 3 21 3 21 9"></polyline>
							<line x1="10" y1="14" x2="21" y2="3"></line>
						</svg>
						<span class="site-link-text">
							<span class="site-link-label">Visite o site da Panther</span>
							<span class="site-link-url">panther.com.br</span>
						</span>
					</a>
				</li>
				{#if isScrolled}
					<li class="mobile-menu-quote-btn-container">
						<button class="mobile-menu-quote-btn" on:click={() => { scrollToQuote(); toggleMobileMenu(); }}>
							{#if quoteStep > 0}
								Continuar Orçamento
							{:else}
								Começar Orçamento
							{/if}
						</button>
					</li>
				{/if}
			</ul>

			{#if isScrolled}
				<div class="nav-cta-section">
					<div class="nav-promo-text">
						<span class="promo-badge">Descontos Exclusivos</span>
						<span class="promo-message">Garanta seu orçamento agora!</span>
					</div>
					<div class="nav-cta-buttons">
						<a href="tel:+551239518505" class="nav-btn-call-simple">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 0.4rem;">
								<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
							</svg>
							(12) 3951-8505
						</a>
						<button class="nav-btn-quote" on:click={scrollToQuote}>
							{#if quoteStep > 0}
								Continuar Orçamento
							{:else}
								Começar Orçamento
							{/if}
						</button>
					</div>
				</div>
			{/if}
		</div>
	</nav>

	<!-- Hero Section -->
	<section class="hero">
		<div class="hero-background"></div>
		<div class="container hero-container" class:full-width={quoteStep > 0}>
			<div class="hero-content desktop-hero-content" class:hidden={quoteStep > 0}>
				<h1 class="hero-title">Solução completa<br/>para a sua <span class="highlight">segurança</span></h1>
				<p class="hero-subtitle">Sistema de Alarme Monitorado 24 horas com Aplicativo incluso e diversas integrações: Câmeras, Teledisparo, Seguros opcionais e muito mais. Tudo isso acompanhado pela maior central de monitoramento da região, com mais de 60 viaturas de pronta resposta.</p>
				<p class="hero-discount">Descontos Exclusivos por tempo limitado*</p>
			</div>

			<div class="mobile-logo-container">
				<img src="panther-logo.svg" alt="Panther" class="mobile-hero-logo" />
			</div>

			<div class="quote-card" class:expanded={quoteStep > 0}>
				<div class="quote-slider">
					<!-- Initial Screen -->
					<div class="quote-screen" class:active={quoteStep === 0}>
						<button class="btn-quote-main" on:click={startQuote}>
							CALCULE
							<span class="arrow">→</span>
						</button>

						<div class="quote-header">
							<p>Calcule agora o preço do seu <span style="white-space: nowrap">sistema de segurança</span></p>
						</div>

						<div class="quote-divider">
							<span>Se preferir, fazemos o seu orçamento por <strong>Telefone</strong></span>
							<p class="divider-subtitle">Insira seu número abaixo</p>
						</div>

						<div class="phone-input-group">
							<input
								type="tel"
								bind:value={phone}
								on:input={handlePhoneInput}
								placeholder="(12) 99999-9999"
								class="phone-input"
								maxlength="15"
							/>
							<button class="btn-submit" on:click={handlePhoneSubmit}>
								ENVIAR
								<span class="submit-arrow">→</span>
							</button>
						</div>

						<div class="quote-footer">
							<p class="call-label">LIGUE</p>
							<a href="tel:+551239518505" class="phone-number">
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="phone-icon">
									<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
								</svg>
								(12) 3951-8505
							</a>
							<p class="disclaimer-text">Atendimento de segunda a sábado.</p>
						</div>
					</div>

					<!-- Question Screens -->
					{#if quoteStep > 0 && currentNode && !currentNode.isFinal}
						<div class="quote-screen active">
							<div class="question-content">
								<div class="question-scrollable">
									<div class="progress-bar">
										<div class="progress-fill" style="width: {progressPercentage}%"></div>
									</div>


									<div class="question-header">
										<h2 class="form-label">{currentNode.question}</h2>
									</div>

									{#if currentNode.isMessage}
										<!-- Message Screen -->
										<div class="message-screen">
											<div class="message-content">
												<p class="message-text">{currentNode.message}</p>
											</div>
											<button class="btn-quote-main btn-continue" on:click={continueFromMessage}>
												CONTINUAR
												<span class="arrow">→</span>
											</button>
										</div>
									{:else if currentNode.isForm}
										<!-- Form Screen -->
										<div class="form-screen">
											<div class="form-group">
												{#if currentNode.formType === 'name'}
													<input
														type="text"
														id="client-name"
														bind:value={clientName}
														placeholder="Digite seu nome"
														class="form-input form-input-large"
													/>
												{:else if currentNode.formType === 'company'}
													<input
														type="text"
														id="company-name"
														bind:value={companyName}
														placeholder="Digite o nome"
														class="form-input form-input-large"
													/>
												{:else if currentNode.formType === 'representante'}
													<input
														type="text"
														id="representante-name"
														bind:value={representanteName}
														placeholder="Digite o nome do representante"
														class="form-input form-input-large"
													/>
												{:else if currentNode.formType === 'other'}
													<input
														type="text"
														id="other-source"
														bind:value={otherSource}
														placeholder="Digite como nos conheceu"
														class="form-input form-input-large"
													/>
												{:else if currentNode.formType === 'phone'}
													<input
														type="tel"
														id="whatsapp-phone"
														bind:value={whatsappPhone}
														on:input={handlePhoneInput}
														placeholder="(12) 99999-9999"
														class="form-input form-input-large"
														maxlength="15"
													/>
												{:else if currentNode.formType === 'email'}
													<input
														type="email"
														id="contact-email"
														bind:value={email}
														placeholder="seu@email.com"
														class="form-input form-input-large"
													/>
												{/if}
											</div>
											<button class="btn-quote-main btn-continue" on:click={continueFromForm}>
												CONTINUAR
												<span class="arrow">→</span>
											</button>
										</div>
									{:else if currentNode.isMultiSelect}
										<!-- Multi-Select Options Grid -->
										<p class="selection-hint">Selecione uma ou mais opções</p>
										<div class="options-grid multi-select">
											{#each currentNode.options as option}
												<button
													class="option-card"
													class:selected={selectedServices.includes(option.id)}
													on:click={() => handleMultiSelectToggle(option.id)}
												>
													<div class="checkbox-indicator">
														{#if selectedServices.includes(option.id)}
															<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
																<polyline points="20 6 9 17 4 12"/>
															</svg>
														{/if}
													</div>
													<div class="option-icon">{@html option.icon}</div>
													<div class="option-content">
														<h3>{option.label}</h3>
														<p>{option.description}</p>
													</div>
												</button>
											{/each}
										</div>
										<p class="selected-count">{selectedServices.length} selecionado{selectedServices.length !== 1 ? 's' : ''}</p>
										<button class="btn-quote-main btn-continue" on:click={continueFromMultiSelect} disabled={selectedServices.length === 0}>
											CONTINUAR
											<span class="arrow">→</span>
										</button>
									{:else}
										<!-- Options Grid -->
										<div class="options-grid" class:grid-4={currentNode === decisionTree.property_type}>
											{#each currentNode.options as option}
												<button class="option-card" on:click={() => selectOption(option)}>
													<div class="option-icon">{@html option.icon}</div>
													<div class="option-content">
														<h3>{option.label}</h3>
														<p>{option.description}</p>
													</div>
												</button>
											{/each}
										</div>
									{/if}
								</div>

								<div class="voltar-button-wrapper">
									<button class="btn-back" on:click={goBack}>
										← Voltar
									</button>
								</div>
							</div>
						</div>
					{/if}

					<!-- Final Screen - Contact Form -->
					{#if currentNode && currentNode.isFinal}
						<div class="quote-screen active">
							<div class="question-content">
								<div class="question-scrollable">
									<div class="progress-bar">
										<div class="progress-fill" style="width: 100%"></div>
									</div>

									<div class="question-header">
										<div class="success-icon">
											<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
												<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
											</svg>
										</div>
										<h2 class="form-label">Perfeito! Deixe seu número para contato</h2>
										<p class="form-sublabel">
											{#if answers.propertyType === 'condominio' || answers.propertyType === 'industria'}
												Vamos preparar um orçamento personalizado e nosso time de Engenharia entrará em contato.
											{:else}
												Vamos preparar um orçamento personalizado e nosso time comercial entrará em contato.
											{/if}
										</p>
									</div>

									<div class="final-form">
										<div class="form-group">
											<input
												type="tel"
												id="phone-final"
												bind:value={phone}
												on:input={handlePhoneInput}
												placeholder="(12) 99999-9999"
												class="form-input form-input-large"
												maxlength="15"
											/>
										</div>

										<button class="btn-quote-main btn-continue" on:click={handlePhoneSubmit}>
											FINALIZAR
											<span class="arrow">→</span>
										</button>

										<p class="privacy-notice">📞 Entraremos em contato em breve com seu orçamento personalizado.</p>
									</div>
								</div>

								<div class="voltar-button-wrapper">
									<button class="btn-back" on:click={goBack}>
										← Voltar
									</button>
								</div>
							</div>
						</div>
					{/if}
				</div>
			</div>

		</div>

		<!-- Tagline -->
		<div class="hero-tagline">
			</div>
	</section>

	<!-- Trusted Companies Section -->
	<section class="trusted-companies">
		<div class="container">
			<div class="section-header">
				<h2>Empresas que confiam na Panther</h2>
				<p>Milhares de clientes entre residências, condomínios, empresas e fábricas protegidos pela tecnologia Panther Segurança</p>
			</div>

			<div class="logos-container">
				<div class="logos-track">
					{#each [...trustedCompanies, ...trustedCompanies] as company}
						<div class="logo-item">
							{#if company.logo}
								<img src={company.logo} alt={company.name} />
							{:else}
								<div class="logo-placeholder">{company.name}</div>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- Mobile Hero Content Section -->
	<section class="mobile-hero-content-section">
		<div class="container">
			<div class="hero-content" class:hidden={quoteStep > 0}>
				<h1 class="hero-title">Solução completa<br/>para a sua <span class="highlight">segurança</span></h1>
				<p class="hero-subtitle">Sistema de Alarme Monitorado 24 horas com Aplicativo incluso e diversas integrações: Câmeras, Teledisparo, Seguros opcionais e muito mais. Tudo isso acompanhado pela maior central de monitoramento da região, com mais de 60 viaturas de pronta resposta.</p>
				<p class="hero-discount">Descontos Exclusivos por tempo limitado*</p>
			</div>
		</div>
	</section>

	<!-- Stats Section -->
	<section class="stats">
		<div class="container stats-grid">
			{#each stats as stat}
				<div class="stat-item">
					<div class="stat-value">{stat.value}</div>
					<div class="stat-label">{stat.label}</div>
				</div>
			{/each}
		</div>
	</section>

	<!-- Panther Alarme Section -->
	<section id="solucoes" class="panther-alarme">
		<div class="container">
			<div class="panther-alarme-header">
				<h2>Alarme Panther com Padrão Bancário</h2>
				<p>A melhor tecnologia disponível no mercado, com a estrutura mais completa e profissionais altamente qualificados. Protegemos você, sua empresa e sua família com excelência em segurança eletrônica, garantindo tranquilidade 24 horas por dia, todos os dias.</p>
			</div>

			<div class="panther-alarme-hero">
				<img src="panther-products.png" height="80%" alt="Panther Alarme - Produtos" class="panther-products-image" />
			</div>

			<!-- Defense Features Slider -->
			<div class="defense-features-slider">
				<button class="defense-slider-arrow defense-arrow-left" on:click={prevDefenseSlide} aria-label="Anterior">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<polyline points="15 18 9 12 15 6"></polyline>
					</svg>
				</button>

				<div class="defense-slide-container">
					{#each defenseFeatures as feature, index}
						<div class="defense-slide" class:active={currentDefenseSlide === index} class:full-width-image={feature.fullWidth} class:no-image={!feature.image && !feature.multiImage}>
							<div class="defense-slide-header">
								<h4>{feature.title}</h4>
							</div>
							<div class="defense-slide-body">
								{#if feature.multiImage && feature.images}
									<div class="defense-slide-image multi-image">
										{#each feature.images as img}
											<img src={img} alt={feature.subtitle} />
										{/each}
									</div>
								{:else if feature.image}
									<div class="defense-slide-image" class:full-width={feature.fullWidth}>
										<img src={feature.image} alt={feature.subtitle} />
									</div>
								{/if}
								<div class="defense-slide-content" class:full-content={!feature.image}>
									<h5>{feature.subtitle}</h5>
									<ul class="defense-features-list">
										{#each feature.description.split('. ').filter(item => item.length > 0) as point}
											<li>
												<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" class="check-icon">
													<polyline points="20 6 9 17 4 12"></polyline>
												</svg>
												{point}{point.endsWith('.') ? '' : '.'}
											</li>
										{/each}
									</ul>
								</div>
							</div>
						</div>
					{/each}
				</div>

				<button class="defense-slider-arrow defense-arrow-right" on:click={nextDefenseSlide} aria-label="Próximo">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<polyline points="9 18 15 12 9 6"></polyline>
					</svg>
				</button>

				<!-- Slider dots -->
				<div class="defense-slider-dots">
					{#each defenseFeatures as feature, index}
						<button
							class="defense-dot"
							class:active={currentDefenseSlide === index}
							on:click={() => goToDefenseSlide(index)}
							aria-label={`Ir para slide ${index + 1}`}
						></button>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Bar 1 -->
	<section class="cta-bar">
		<div class="container cta-bar-content">
			<div class="cta-bar-text">
				<h3>{ctaBars[0].title}</h3>
				<p class="cta-bar-discount">{ctaBars[0].discount}</p>
			</div>
			<button class="cta-bar-button" on:click={scrollToQuote}>
				{ctaBars[0].buttonText}
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<circle cx="12" cy="12" r="10"></circle>
					<polyline points="12 16 16 12 12 8"></polyline>
					<line x1="8" y1="12" x2="16" y2="12"></line>
				</svg>
			</button>
		</div>
	</section>

	<!-- Panther Segurança Section -->
	<section class="panther-seguranca">
		<div class="panther-seguranca-overlay"></div>
		<div class="panther-seguranca-content">
			<div class="panther-seguranca-brand">
				<img src="panther-logo.svg" alt="Panther" class="panther-seguranca-logo" />
			</div>

			<div class="panther-about-text">
				<h3 class="about-title">A História da Panther Segurança</h3>
				<p>Fundada em 1995, a Panther Segurança nasceu com um propósito claro: proteger as vítimas da criminalidade na região, trazendo tranquilidade em tempos difíceis. Desde o início, a busca por uma qualidade acima dos padrões impulsionou um crescimento constante, conquistando clientes, um nome sólido e, acima de tudo, a confiança total nos serviços prestados.</p>
				<br />
				<p>A Panther não vende alarmes — entrega segurança com responsabilidade. Investe para proteger. E sabe que tradição não se compra: constrói-se, ano após ano, com compromisso e cuidado.</p>
				<br />
				<h4 class="about-end">Panther Segurança<br>30 anos protegendo o que importa</h4>
			</div>

			<div class="stats-hexagon-container">
				<div class="hexagons-track">
					<div class="hexagon-stat" style="--delay: 0s;">
						<div class="hexagon-inner">
							<div class="stat-number">+400</div>
							<div class="stat-label">Colaboradores</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.1s;">
						<div class="hexagon-inner">
							<div class="stat-number">+30</div>
							<div class="stat-label">Anos no Mercado</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.2s;">
						<div class="hexagon-inner">
							<div class="stat-number">+2000</div>
							<div class="stat-label">Arrombamentos Evitados</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.3s;">
						<div class="hexagon-inner">
							<div class="stat-number">24/7</div>
							<div class="stat-label">Infraestrutura Redundante</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.4s;">
						<div class="hexagon-inner">
							<div class="stat-number">+60</div>
							<div class="stat-label">Viaturas</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.5s;">
						<div class="hexagon-inner">
							<div class="stat-number">+R$ 1M</div>
							<div class="stat-label">Evitados em Furtos</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.6s;">
						<div class="hexagon-inner">
							<div class="stat-number">+700m²</div>
							<div class="stat-label">Base Operacional</div>
						</div>
					</div>
					<div class="hexagon-stat" style="--delay: 0.7s;">
						<div class="hexagon-inner">
							<div class="stat-label-only">Viaturas<br/>Blindadas</div>
						</div>
					</div>
					<!-- Duplicate for infinite scroll -->
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">+400</div>
							<div class="stat-label">Colaboradores</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">+30</div>
							<div class="stat-label">Anos no Mercado</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">+2000</div>
							<div class="stat-label">Arrombamentos Evitados</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">24/7</div>
							<div class="stat-label">Infraestrutura Redundante</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">+60</div>
							<div class="stat-label">Viaturas</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">+R$ 1M</div>
							<div class="stat-label">Evitados em Furtos</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-number">+700m²</div>
							<div class="stat-label">Base Operacional</div>
						</div>
					</div>
					<div class="hexagon-stat">
						<div class="hexagon-inner">
							<div class="stat-label-only">Viaturas<br/>Blindadas</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Comparison Section -->
	<section class="comparison">
		<div class="container">
			<div class="section-header">
				<h2>Por que escolher a Panther?</h2>
				<p>Veja o que nos torna únicos quando se trata de segurança</p>
			</div>

			<div class="comparison-table">
				<div class="comparison-header">
					<div class="header-feature">Recursos</div>
					<div class="header-company panther-header">
						<img src="panther-logo.svg" alt="Panther" class="comparison-logo" />
						<span>Alarme Padrão Bancário</span>
					</div>
					<div class="header-company competitors-header">
						<span>Smart Alarme</span>
					</div>
				</div>

				<div class="comparison-body">
					{#each comparisonFeatures as item, index}
						<div class="comparison-row" class:even={index % 2 === 0}>
							<div class="feature-name">{item.feature}</div>
							<div class="feature-value panther-value">
								{#if item.panther}
									<svg class="check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="20 6 9 17 4 12"></polyline>
									</svg>
								{:else}
									<svg class="cross-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<line x1="18" y1="6" x2="6" y2="18"></line>
										<line x1="6" y1="6" x2="18" y2="18"></line>
									</svg>
								{/if}
							</div>
							<div class="feature-value competitors-value">
								{#if item.competitors}
									<svg class="check-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
										<polyline points="20 6 9 17 4 12"></polyline>
									</svg>
								{:else}
									<svg class="cross-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
										<line x1="18" y1="6" x2="6" y2="18"></line>
										<line x1="6" y1="6" x2="18" y2="18"></line>
									</svg>
								{/if}
							</div>
						</div>
					{/each}
				</div>

				<div class="comparison-footer">
					<button class="btn-comparison-cta" on:click={scrollToQuote}>
						Solicitar Orçamento
						<span class="arrow">→</span>
					</button>
				</div>
			</div>
		</div>
	</section>

	<!-- Testimonials Section -->
	<section class="testimonials">
		<div class="container">
			<div class="section-header">
				<h2>O que nossos clientes dizem</h2>
				<p>Depoimentos reais de quem confia na Panther para sua segurança</p>
			</div>

			<div class="testimonials-container">
				<button class="testimonial-arrow testimonial-prev" on:click={prevTestimonial} aria-label="Depoimento anterior">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<polyline points="15 18 9 12 15 6"></polyline>
					</svg>
				</button>

				<div class="testimonials-grid" on:touchstart={handleTouchStart} on:touchend={handleTouchEnd}>
					{#each visibleTestimonials as testimonial (testimonial.id)}
						<div class="testimonial-card">
							<div class="quote-icon">
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
									<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
								</svg>
							</div>

							<p class="testimonial-text">{testimonial.text}</p>

							<div class="testimonial-rating">
								{#each Array(testimonial.rating) as _, i}
									<span class="star">★</span>
								{/each}
							</div>

							<div class="testimonial-author">
								<div class="author-avatar">
									{#if testimonial.image}
										<img src={testimonial.image} alt={testimonial.name} />
									{:else}
										<div class="avatar-placeholder">{testimonial.name.charAt(0)}</div>
									{/if}
								</div>
								<div class="author-info">
									<h4 class="author-name">{testimonial.name}</h4>
									<p class="author-meta">{testimonial.age}</p>
								</div>
							</div>
						</div>
					{/each}
				</div>

				<button class="testimonial-arrow testimonial-next" on:click={nextTestimonial} aria-label="Próximo depoimento">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<polyline points="9 18 15 12 9 6"></polyline>
					</svg>
				</button>
			</div>

			<div class="testimonial-dots">
				{#each Array(Math.ceil(testimonials.length / itemsPerPage)) as _, i}
					<button
						class="dot"
						class:active={i === currentTestimonialIndex}
						on:click={() => currentTestimonialIndex = i}
						aria-label="Ir para página {i + 1}"
					></button>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section id="contato" class="cta-section">
		<div class="container cta-content">
			<h2>Calcule o preço<br/>do seu novo e robusto Sistema de Segurança</h2>
			<p>Junte-se a milhares de clientes satisfeitos que confiam na Panther para suas necessidades de segurança.</p>
			<div class="cta-buttons">
				<button class="btn btn-primary-white" on:click={scrollToQuote}>Solicitar Orçamento</button>
			</div>
		</div>
	</section>

	<!-- Footer -->
	<footer class="footer">
		<div class="container footer-content">
			<div class="footer-section">
				<a href="#" class="footer-logo">
					<img src="panther-logo.svg" alt="Panther" class="footer-logo-img footer-logo-white" />
				</a>
					<div class="footer-social">
					<a href="https://www.facebook.com/PantherSeguranca24H/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
							<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
						</svg>
					</a>
					<a href="https://instagram.com/pantherseguranca" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
							<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
						</svg>
					</a>
					<a href="https://www.linkedin.com/company/panther-seguran%C3%A7aemonitoramento/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
							<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
						</svg>
					</a>
				</div>
			</div>
			<div class="footer-section">
				<h4>Endereços</h4>
				<div class="footer-address">
					<p><strong>Base Jacareí</strong></p>
					<p>Av. Bruno Decária, 111</p>
					<p>Parque Brasil</p>
					<p>Jacareí - SP, 12328-410</p>
				</div>
				<div class="footer-address">
					<p><strong>Base São José dos Campos</strong></p>
					<p>Av. Comendador Vicente de Paulo Penido, 304</p>
					<p>Sala 1 - Jardim Aquarius</p>
					<p>São José dos Campos - SP, 12246-856</p>
				</div>
			</div>
			<div class="footer-section">
				<h4>Contato</h4>
				<ul>
					<li><a href="tel:+551239518505">SAC: (12) 3951-8505</a></li>
					<li><a href="mailto:contato@panther.com.br">contato@panther.com.br</a></li>
				</ul>
			</div>
			<div class="footer-section">
				<h4>Links Rápidos</h4>
				<ul>
					<li><a href="https://panther.com.br" target="_blank">Site Panther</a></li>
				</ul>
			</div>
		</div>
		<div class="footer-bottom">
			<div class="container">
				<p>&copy; 1995 - 2026 Panther Segurança. Todos os direitos reservados.</p>
				<p class="footer-legal">Panther Segurança Especializada Ltda - CNPJ: 10.956.451/0001-50</p>
			</div>
		</div>
	</footer>

	<!-- Back to Top Button -->
	{#if showBackToTop}
		<button class="back-to-top" on:click={scrollToTop} aria-label="Voltar ao topo">
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
				<line x1="12" y1="19" x2="12" y2="5"></line>
				<polyline points="5 12 12 5 19 12"></polyline>
			</svg>
		</button>
	{/if}

	<!-- Custom Confirmation Popup -->
	{#if showConfirmation}
		<div class="confirmation-overlay" on:click={closeConfirmation}>
			<div class="confirmation-modal" class:error={confirmationType === 'error'} on:click|stopPropagation>
				<div class="confirmation-header" class:error={confirmationType === 'error'}>
					{#if confirmationType === 'error'}
						<svg class="confirmation-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<circle cx="12" cy="12" r="10"></circle>
							<line x1="15" y1="9" x2="9" y2="15"></line>
							<line x1="9" y1="9" x2="15" y2="15"></line>
						</svg>
					{:else}
						<svg class="confirmation-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
							<polyline points="22 4 12 14.01 9 11.01"></polyline>
						</svg>
					{/if}
				</div>
				<div class="confirmation-content">
					{#if confirmationType === 'error'}
						<h3 class="confirmation-title error">Atenção!</h3>
					{:else}
						<h3 class="confirmation-title">Obrigado!</h3>
					{/if}
					<p class="confirmation-message">{confirmationMessage}</p>
				</div>
				{#if confirmationCallback}
					<div class="confirmation-buttons">
						<button class="confirmation-button confirmation-button-secondary" on:click={closeConfirmation}>
							Voltar ao Início
						</button>
						<button class="confirmation-button confirmation-button-primary" on:click={() => { closeConfirmation(); scrollToQuote(); }}>
							Novo Orçamento
						</button>
					</div>
					<a href="https://panther.com.br" target="_blank" rel="noopener noreferrer" class="confirmation-link">
						Visitar Site da Panther
					</a>
				{:else}
					<button class="confirmation-button confirmation-button-primary" on:click={closeConfirmation}>
						OK
					</button>
					<a href="https://panther.com.br" target="_blank" rel="noopener noreferrer" class="confirmation-link">
						Visitar Site da Panther
					</a>
				{/if}
			</div>
		</div>
	{/if}

	<!-- Loading overlay -->
	{#if isSubmitting}
		<div class="loading-overlay">
			<div class="loading-modal">
				<div class="spinner"></div>
				<p class="loading-message">Processando sua solicitação...</p>
				<p class="loading-submessage">Aguarde um momento, estamos nos comunicando com nossos servidores</p>
			</div>
		</div>
	{/if}
</div>

<style>
	:global(body) {
		overflow-x: hidden;
	}

	:global(img) {
		max-width: 100%;
		height: auto;
	}

	:global(*) {
		box-sizing: border-box;
	}

	.app {
		width: 100%;
		overflow-x: hidden;
	}

	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 clamp(1rem, 5vw, 2rem);
		width: 100%;
	}

	/* Navigation */
	.navbar {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		background: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(10px);
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
		z-index: 1000;
		transition: all 0.3s ease;
	}

	.navbar.scrolled {
		background: rgba(26, 26, 26, 0.98);
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	}

	.navbar.scrolled .logo-img {
		filter: brightness(0) invert(1);
	}

	.nav-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0 2rem;
		gap: 2rem;
	}

	.logo {
		display: flex;
		align-items: center;
	}

	.logo-img {
		height: 120px;
		width: auto;
		transition: all 0.3s ease;
	}

	.navbar.scrolled .logo-img {
		height: 60px;
	}

	.mobile-call-btn {
		display: none;
		align-items: center;
		gap: 0.5rem;
		background: transparent;
		color: #1a1a1a;
		padding: 0.5rem 0.75rem;
		border-radius: 6px;
		font-size: 0.875rem;
		font-weight: 700;
		text-decoration: none;
		transition: all 0.3s ease;
	}

	.mobile-call-btn svg {
		stroke: #1a1a1a;
	}

	.mobile-call-btn:hover {
		background: rgba(150, 191, 19, 0.1);
	}

	.navbar.scrolled .mobile-call-btn {
		color: white;
	}

	.navbar.scrolled .mobile-call-btn svg {
		stroke: white;
	}

	.mobile-menu-btn {
		display: none;
		background: none;
		border: none;
		cursor: pointer;
		padding: 0.5rem;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.6rem;
	}

	.menu-text {
		font-size: 0.65rem;
		font-weight: 600;
		letter-spacing: 0.5px;
		color: #1a1a1a;
		text-transform: uppercase;
	}

	.mobile-menu-close {
		display: none;
		position: absolute;
		top: 1.5rem;
		right: 1.5rem;
		background: none;
		border: none;
		cursor: pointer;
		padding: 0.5rem;
		width: 40px;
		height: 40px;
		color: #1a1a1a;
		transition: all 0.3s ease;
	}

	.mobile-menu-close svg {
		width: 24px;
		height: 24px;
	}

	.mobile-menu-close:hover {
		transform: rotate(90deg);
		color: #96bf13;
	}

	.mobile-menu-overlay {
		display: none;
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100vh;
		background: rgba(0, 0, 0, 0.5);
		z-index: 998;
		backdrop-filter: blur(2px);
	}

	.hamburger {
		display: block;
		width: 22px;
		height: 2px;
		background: #1a1a1a;
		position: relative;
		border-radius: 2px;
	}

	.hamburger::before,
	.hamburger::after {
		content: '';
		position: absolute;
		width: 22px;
		height: 2px;
		background: #1a1a1a;
		left: 0;
		border-radius: 2px;
	}

	.hamburger::before {
		top: -7px;
	}

	.hamburger::after {
		bottom: -7px;
	}

	.nav-links {
		display: flex;
		list-style: none;
		gap: 2rem;
		align-items: center;
	}

	@media (min-width: 769px) {
		.navbar.scrolled .nav-links {
			display: none;
		}
	}

	.nav-links a {
		color: #1a1a1a;
		font-weight: 500;
		padding: 0.5rem 0;
		position: relative;
	}

	.nav-links a::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 0;
		height: 2px;
		background: #1a1a1a;
		transition: width 0.3s ease;
	}

	.nav-links a:hover::after {
		width: 100%;
	}

	.nav-site-link {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem !important;
		border: 1px solid #1a1a1a;
		border-radius: 6px;
		transition: all 0.3s ease;
	}

	.nav-site-link:hover {
		background: #1a1a1a;
		color: white;
		transform: translateY(-2px);
	}

	.nav-site-link::after {
		display: none !important;
	}

	.site-icon {
		width: 18px;
		height: 18px;
		flex-shrink: 0;
	}

	.site-link-text {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		line-height: 1.2;
	}

	.site-link-label {
		font-size: 0.75rem;
		font-weight: 400;
		opacity: 0.8;
	}

	.site-link-url {
		font-size: 0.9rem;
		font-weight: 600;
	}

	.mobile-menu-quote-btn-container {
		margin-top: 1rem;
		padding-top: 1rem;
		border-top: 1px solid rgba(0, 0, 0, 0.1);
	}

	.mobile-menu-quote-btn {
		width: 100%;
		padding: 1rem 1.5rem;
		background: #96bf13;
		color: #1a1a1a;
		border: none;
		border-radius: 8px;
		font-weight: 700;
		font-size: 1rem;
		cursor: pointer;
		transition: all 0.3s ease;
		text-align: center;
	}

	.mobile-menu-quote-btn:hover {
		background: #82a510;
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(150, 191, 19, 0.3);
	}

	.nav-phone-container {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.2rem;
	}

	.nav-phone-label {
		font-size: 0.7rem;
		color: #666;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.nav-phone {
		color: #1a1a1a !important;
		padding: 0 !important;
		font-weight: 800;
		letter-spacing: 0.3px;
		transition: all 0.3s ease;
		font-size: 0.95rem;
		position: relative;
		border-bottom: 2px solid #96bf13;
		padding-bottom: 0.2rem !important;
	}

	.nav-phone::after {
		display: none;
	}

	.nav-phone:hover {
		color: #96bf13 !important;
		border-bottom-color: #82a510;
	}

	/* Scrolled CTA Section */
	.nav-cta-section {
		display: flex;
		align-items: center;
		gap: 2rem;
		flex: 1;
		justify-content: flex-end;
	}

	.nav-promo-text {
		display: flex;
		align-items: center;
		gap: 0.8rem;
	}

	.promo-badge {
		background: #96bf13;
		color: #1a1a1a;
		padding: 0.4rem 0.8rem;
		border-radius: 20px;
		font-weight: 700;
		font-size: 0.85rem;
		letter-spacing: 0.5px;
		animation: pulse 2s ease-in-out infinite;
	}

	@keyframes pulse {
		0%, 100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.05);
		}
	}

	.promo-message {
		color: white;
		font-weight: 600;
		font-size: 0.95rem;
		white-space: nowrap;
	}

	.nav-cta-buttons {
		display: flex;
		gap: 1rem;
		align-items: center;
	}

	.nav-btn-call,
	.nav-btn-call-simple {
		padding: 0.7rem 1.5rem;
		border-radius: 6px;
		font-weight: 600;
		font-size: 0.9rem;
		background: white;
		color: #1a1a1a;
		border: 2px solid white;
		text-decoration: none;
		white-space: nowrap;
		transition: all 0.3s ease;
		display: inline-flex;
		align-items: center;
	}

	.nav-btn-call-simple:hover {
		background: transparent;
		color: white;
	}

	.nav-btn-call-simple:hover svg {
		stroke: white;
	}

	.nav-btn-quote {
		padding: 0.7rem 1.5rem;
		border-radius: 6px;
		font-weight: 600;
		font-size: 0.9rem;
		cursor: pointer;
		transition: all 0.3s ease;
		border: 2px solid transparent;
		white-space: nowrap;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		text-decoration: none;
	}

	.nav-btn-call {
		background: transparent;
		color: white;
		border: none;
		padding: 0.5rem 1rem;
	}

	.nav-call-content {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.2rem;
	}

	.nav-call-label {
		font-size: 0.7rem;
		color: rgba(255, 255, 255, 0.7);
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.nav-call-number {
		font-size: 0.95rem;
		color: white;
		font-weight: 800;
		letter-spacing: 0.3px;
		border-bottom: 2px solid #96bf13;
		padding-bottom: 0.2rem;
		transition: all 0.3s ease;
	}

	.nav-btn-call:hover .nav-call-number {
		color: #96bf13;
		border-bottom-color: #82a510;
	}

	.nav-btn-quote {
		background: #96bf13;
		color: #1a1a1a;
		border-color: #96bf13;
	}

	.nav-btn-quote:hover {
		background: #82a510;
		transform: translateY(-2px);
		box-shadow: 0 4px 16px rgba(150, 191, 19, 0.4);
	}


	/* Hero Section */
	.hero {
		position: relative;
		min-height: 100vh;
		display: flex;
		align-items: center;
		padding-top: 100px;
		padding-bottom: 4rem;
		overflow: hidden;
	}

	.hero-background {
		position: absolute;
		inset: 0;
		background:
			linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.5) 100%),
			radial-gradient(circle at 20% 50%, rgba(150, 191, 19, 0.1) 0%, transparent 50%),
			url('../base-panther.jpg');
		background-size: cover, cover, cover;
		background-position: center;
		z-index: 0;
	}

	.hero-background::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(90deg, rgba(0, 0, 0, 0.15) 0%, transparent 60%);
	}

	.hero-container {
		position: relative;
		z-index: 1;
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 3rem;
		align-items: center;
		transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
		width: 100%;
		min-height: 70vh;
	}

	.hero-container.full-width {
		grid-template-columns: 1fr;
		max-width: 1000px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	.hero-content {
		color: white;
		transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
		text-align: center;
	}

	.hero-content.hidden {
		opacity: 0;
		transform: translateX(-50px);
		pointer-events: none;
		position: absolute;
	}

	.hero-title {
		font-size: clamp(2rem, 4vw, 3rem);
		font-weight: 700;
		line-height: 1.3;
		margin-bottom: 1.5rem;
		text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
	}

	.hero-title .highlight {
		color: #96bf13;
		font-weight: 800;
	}

	.mobile-logo-container {
		display: none;
	}

	.mobile-hero-logo {
		width: auto;
		height: 80px;
	}

	.mobile-hero-content-section {
		display: none;
	}

	.hero-subtitle {
		font-size: clamp(1.1rem, 2.2vw, 1.35rem);
		color: rgba(255, 255, 255, 0.95);
		font-weight: 800;
		line-height: 1.7;
		margin-bottom: 1.5rem;
		max-width: 90%;
		margin-left: auto;
		margin-right: auto;
		text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
	}

	.hero-discount {
		font-size: clamp(1.1rem, 2vw, 1.3rem);
		color: #96bf13;
		font-weight: 700;
		margin-top: 1rem;
	}

	/* Hero Tagline */
	.hero-tagline {
		position: absolute;
		bottom: 8rem;
		left: 0;
		right: 0;
		text-align: center;
		padding: 0 2rem;
		z-index: 10;
	}

	.tagline-text {
		font-size: clamp(1.1rem, 2.5vw, 1.4rem);
		color: white;
		font-weight: 700;
		letter-spacing: 0.5px;
		margin: 0;
		text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
	}

	.tagline-year {
		color: #96bf13;
		font-weight: 800;
	}

	/* Quote Card */
	.quote-card {
		background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);
		border-radius: 32px;
		padding: 2.5rem;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
		overflow: hidden;
		position: relative;
		height: 650px;
		border: 1px solid rgba(0, 0, 0, 0.05);
		transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
		width: 100%;
		display: flex;
		flex-direction: column;
	}

	@media (max-width: 768px) {
		.quote-card {
			background: white !important;
			border-radius: 24px !important;
			padding: 1.5rem !important;
			box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
			border: 1px solid rgba(0, 0, 0, 0.05) !important;
			height: auto !important;
			width: 100% !important;
			max-width: none !important;
			margin: 0 !important;
			overflow: visible !important;
		}
	}

	.quote-card.expanded {
		background: transparent;
		border: none;
		box-shadow: none;
		padding: 3rem;
		height: auto;
		border-radius: 0;
	}

	.quote-slider {
		position: relative;
		width: 100%;
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	.quote-screen {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		transform: translateX(100%);
		transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
		            transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
		pointer-events: none;
		display: flex;
		flex-direction: column;
	}

	.quote-screen.active {
		opacity: 1;
		transform: translateX(0);
		pointer-events: all;
		position: relative;
	}

	.quote-screen:first-child.active {
		transform: translateX(0);
	}

	.quote-screen:not(.active):first-child {
		transform: translateX(-100%);
	}

	.question-content {
		max-width: 900px;
		width: 100%;
		height: 600px;
		margin: 0 auto;
		background: rgba(255, 255, 255, 0.05);
		padding: 2rem;
		display: flex;
		flex-direction: column;
		position: relative;
		border-radius: 24px;
		backdrop-filter: blur(30px);
		border: 1px solid rgba(255, 255, 255, 0.15);
		box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
		overflow: hidden;
	}

	.question-scrollable {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 0.5rem;
		margin-bottom: 1rem;
	}

	.question-scrollable::-webkit-scrollbar {
		width: 6px;
	}

	.question-scrollable::-webkit-scrollbar-track {
		background: rgba(255, 255, 255, 0.05);
		border-radius: 10px;
	}

	.question-scrollable::-webkit-scrollbar-thumb {
		background: rgba(150, 191, 19, 0.4);
		border-radius: 10px;
	}

	.question-scrollable::-webkit-scrollbar-thumb:hover {
		background: rgba(150, 191, 19, 0.6);
	}

	.progress-bar {
		width: 100%;
		height: 8px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 10px;
		overflow: hidden;
		margin-bottom: 1.75rem;
	}

	.progress-fill {
		height: 100%;
		background: #96bf13;
		border-radius: 10px;
		transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
		box-shadow: 0 0 20px rgba(150, 191, 19, 0.5);
	}

	/* Progress Message Badge */
	.progress-message-badge {
		display: inline-flex;
		align-items: center;
		gap: 0.625rem;
		background: rgba(150, 191, 19, 0.15);
		border: 2px solid #96bf13;
		border-radius: 24px;
		padding: 0.5rem 1.25rem;
		margin: 0 auto 1rem;
		animation: slideInDown 0.5s cubic-bezier(0.4, 0, 0.2, 1);
		font-size: 0.925rem;
	}

	@keyframes slideInDown {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.message-badge-icon {
		width: 22px;
		height: 22px;
		background: #96bf13;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 0.9rem;
		font-weight: 900;
		color: #1a1a1a;
		flex-shrink: 0;
	}

	.progress-message-badge span {
		font-size: 0.9rem;
		font-weight: 600;
		color: #96bf13;
		line-height: 1.3;
	}

	.step-badge {
		display: inline-block;
		background: rgba(150, 191, 19, 0.15);
		color: #96bf13;
		padding: 0.5rem 1.2rem;
		border-radius: 20px;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 1px;
		text-transform: uppercase;
		border: 1px solid rgba(150, 191, 19, 0.3);
		margin-bottom: 2rem;
	}

	/* Modern Quote Card Styles */
	.quote-tabs {
		display: flex;
		background: #f5f5f5;
		border-bottom: 1px solid #e0e0e0;
		overflow-x: auto;
	}

	.quote-tab {
		flex: 1;
		padding: 1rem;
		text-align: center;
		font-size: 0.875rem;
		font-weight: 600;
		color: #666;
		border-bottom: 3px solid transparent;
		transition: all 0.3s ease;
		cursor: pointer;
		white-space: nowrap;
	}

	.quote-tab.active {
		color: #1a1a1a;
		border-bottom-color: #96bf13;
		background: white;
	}

	.quote-tab.highlight {
		background: #c91a1f;
		color: white;
		font-weight: 800;
	}

	.quote-content {
		padding: 2.5rem 2rem;
	}

	.quote-title {
		font-size: 1.75rem;
		font-weight: 800;
		color: #1a1a1a;
		margin-bottom: 0.75rem;
		line-height: 1.3;
	}

	.quote-subtitle {
		font-size: 1rem;
		color: #666;
		margin-bottom: 2rem;
		line-height: 1.5;
	}

	.btn-primary-large {
		width: 100%;
		padding: 1.25rem 2rem;
		background: #96bf13;
		color: #1a1a1a;
		font-size: 1rem;
		font-weight: 700;
		border-radius: 6px;
		border: none;
		cursor: pointer;
		transition: all 0.3s ease;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.btn-primary-large:hover {
		background: #82a510;
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(150, 191, 19, 0.3);
	}

	.or-divider {
		text-align: center;
		margin: 1.5rem 0;
		position: relative;
	}

	.or-divider::before,
	.or-divider::after {
		content: '';
		position: absolute;
		top: 50%;
		width: 45%;
		height: 1px;
		background: #e0e0e0;
	}

	.or-divider::before {
		left: 0;
	}

	.or-divider::after {
		right: 0;
	}

	.or-divider span {
		background: white;
		padding: 0 1rem;
		color: #999;
		font-size: 0.875rem;
	}

	.contact-option {
		margin-bottom: 2rem;
	}

	.contact-label {
		font-size: 0.875rem;
		color: #666;
		margin-bottom: 0.75rem;
		text-align: center;
	}

	.phone-input-row {
		display: flex;
		gap: 0.5rem;
	}

	.modern-input {
		flex: 1;
		padding: 0.875rem 1rem;
		border: 2px solid #e0e0e0;
		border-radius: 6px;
		font-size: 1rem;
		transition: all 0.3s ease;
	}

	.modern-input:focus {
		outline: none;
		border-color: #96bf13;
	}

	.btn-secondary-send {
		padding: 0.875rem 2rem;
		background: #1a1a1a;
		color: white;
		border: none;
		border-radius: 6px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
		font-size: 0.875rem;
		text-transform: uppercase;
	}

	.btn-secondary-send:hover {
		background: #2d2d2d;
		transform: translateY(-2px);
	}

	.call-section {
		text-align: center;
		padding-top: 1.5rem;
		border-top: 1px solid #e0e0e0;
	}

	.call-link {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		color: #96bf13;
		font-size: 1.25rem;
		font-weight: 700;
		text-decoration: none;
		margin-bottom: 0.5rem;
		transition: all 0.3s ease;
	}

	.call-link:hover {
		color: #82a510;
	}

	.call-link svg {
		stroke: #96bf13;
	}

	.call-hours {
		font-size: 0.75rem;
		color: #999;
		margin: 0;
	}

	.quote-header {
		text-align: center;
		margin-bottom: 2rem;
	}

	.quote-header h3 {
		font-size: 2.2rem;
		font-weight: 800;
		color: #1a1a1a;
		margin-bottom: 0.8rem;
		letter-spacing: 1px;
	}

	.quote-header p {
		color: #555;
		font-size: 1rem;
		font-weight: 400;
		line-height: 1.5;
	}

	.btn-quote-main {
		width: 100%;
		padding: 1.4rem 2rem;
		background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
		color: white;
		font-size: 1.1rem;
		font-weight: 700;
		border-radius: 10px;
		border: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
		transition: all 0.3s ease;
		margin-bottom: 2rem;
		letter-spacing: 1.5px;
		text-transform: uppercase;
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
		position: relative;
		overflow: hidden;
	}

	.btn-quote-main::before {
		content: '';
		position: absolute;
		top: 0;
		left: -100%;
		width: 100%;
		height: 100%;
		background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
		transition: left 0.5s ease;
	}

	.btn-quote-main:hover::before {
		left: 100%;
	}

	.btn-quote-main:hover {
		transform: translateY(-3px);
		box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
	}

	.btn-quote-main .arrow {
		font-size: 1.8rem;
		transition: transform 0.3s ease;
	}

	.btn-quote-main:hover .arrow {
		transform: translateX(5px);
	}

	.btn-continue {
		background: #96bf13;
		color: #1a1a1a;
	}

	.btn-continue::before {
		background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
	}

	.btn-continue:hover {
		background: #82a510;
		box-shadow: 0 8px 30px rgba(150, 191, 19, 0.4);
	}

	/* Back Button */
	.voltar-button-wrapper {
		display: flex;
		justify-content: center;
		align-items: center;
		padding-top: 1.5rem;
		background: transparent;
		flex-shrink: 0;
	}

	.btn-back {
		background: rgba(255, 255, 255, 0.1);
		color: rgba(255, 255, 255, 0.9);
		border: 1px solid rgba(255, 255, 255, 0.2);
		padding: 0.8rem 2rem;
		font-size: 1rem;
		font-weight: 600;
		cursor: pointer;
		margin-bottom: 0;
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		transition: all 0.3s ease;
		border-radius: 8px;
	}

	.btn-back:hover {
		background: rgba(255, 255, 255, 0.15);
		color: white;
		border-color: rgba(255, 255, 255, 0.3);
		transform: translateY(-2px);
	}

	/* Question Header */
	.question-header {
		text-align: center;
		margin-bottom: 1.75rem;
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.form-label {
		font-weight: 700;
		color: #ffffff;
		font-size: 1.65rem;
		line-height: 1.3;
		margin: 0;
	}

	.options-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 0.875rem;
		margin-bottom: 1rem;
	}

	.options-grid.grid-4 {
		grid-template-columns: repeat(2, 1fr);
		gap: 0.875rem;
	}

	.option-card {
		position: relative;
		background: rgba(255, 255, 255, 0.95);
		border: 2px solid rgba(0, 0, 0, 0.1);
		border-radius: 12px;
		padding: 1rem 1.25rem;
		cursor: pointer;
		transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
		backdrop-filter: blur(10px);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		min-height: 120px;
		overflow: hidden;
		width: 100%;
	}

	button.option-card {
		appearance: none;
		font: inherit;
		color: inherit;
	}

	.option-card::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(135deg, rgba(150, 191, 19, 0.1) 0%, transparent 100%);
		opacity: 0;
		transition: opacity 0.4s ease;
	}

	.option-card:hover {
		transform: translateY(-4px) scale(1.01);
		border-color: rgba(150, 191, 19, 0.5);
		box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
	}

	.option-card:hover::before {
		opacity: 1;
	}

	.option-card.selected {
		background: rgba(150, 191, 19, 0.15);
		border-color: #96bf13;
		box-shadow: 0 20px 60px rgba(150, 191, 19, 0.3);
	}

	.option-card.selected::before {
		opacity: 1;
		background: linear-gradient(135deg, rgba(150, 191, 19, 0.2) 0%, rgba(150, 191, 19, 0.1) 100%);
	}

	/* Multi-select specific styles */
	.options-grid.multi-select .option-card {
		position: relative;
	}

	.option-card.selected {
		border-color: #96bf13;
		background: rgba(150, 191, 19, 0.15);
		box-shadow: 0 0 0 3px rgba(150, 191, 19, 0.3);
	}

	.checkbox-indicator {
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
		width: 24px;
		height: 24px;
		border: 2px solid rgba(0, 0, 0, 0.2);
		border-radius: 6px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: white;
		transition: all 0.2s ease;
		z-index: 2;
	}

	.option-card.selected .checkbox-indicator {
		background: #96bf13;
		border-color: #96bf13;
	}

	.checkbox-indicator svg {
		width: 16px;
		height: 16px;
		stroke: white;
	}

	.selection-hint {
		text-align: center;
		color: rgba(255, 255, 255, 0.8);
		font-size: 0.95rem;
		margin-bottom: 0.75rem;
	}

	.selected-count {
		text-align: center;
		color: #96bf13;
		font-weight: 600;
		margin: 0.75rem 0;
		font-size: 0.95rem;
	}

	.btn-quote-main:disabled {
		opacity: 0.5;
		cursor: not-allowed;
		transform: none;
	}

	.btn-quote-main:disabled:hover {
		transform: none;
		box-shadow: none;
	}

	.option-card input[type="radio"] {
		position: absolute;
		opacity: 0;
		pointer-events: none;
	}

	.option-icon {
		font-size: 2.75rem;
		margin-bottom: 0.625rem;
		transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
		display: flex;
		align-items: center;
		justify-content: center;
		line-height: 1;
	}

	.option-icon :global(svg) {
		width: 2.75rem;
		height: 2.75rem;
		stroke: currentColor;
	}

	.option-card:hover .option-icon {
		transform: scale(1.1);
	}

	/* Touch-friendly active states for mobile */
	.option-card:active {
		transform: scale(0.98);
		border-color: rgba(150, 191, 19, 0.6);
	}

	@media (hover: none) and (pointer: coarse) {
		/* Mobile touch devices - disable hover effects, use only active */
		.option-card:hover {
			transform: none;
			border-color: rgba(255, 255, 255, 0.15);
			box-shadow: none;
		}

		.option-card:hover::before {
			opacity: 0;
		}

		.option-card:hover .option-icon {
			transform: none;
		}

		.option-card:active {
			transform: scale(0.98);
			border-color: rgba(150, 191, 19, 0.5);
			background: rgba(255, 255, 255, 0.12);
		}

		/* Horizontal layout for mobile - no vertical lift on hover */
		@media (max-width: 768px) {
			.option-card:active {
				transform: scale(0.98);
			}
		}
	}

	.option-card.selected .option-icon {
		transform: scale(1.15);
	}

	.option-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.option-content h3 {
		font-size: 1.05rem;
		font-weight: 700;
		color: #1a1a1a;
		margin-bottom: 0.25rem;
		line-height: 1.3;
	}

	.option-content p {
		font-size: 0.8rem;
		color: rgba(0, 0, 0, 0.65);
		line-height: 1.3;
		margin: 0;
	}

	.option-check {
		position: absolute;
		top: 0.875rem;
		right: 0.875rem;
		transform: scale(0);
		width: 26px;
		height: 26px;
		border-radius: 50%;
		background: #96bf13;
		color: #1a1a1a;
		font-size: 1rem;
		font-weight: 900;
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 0;
		transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
		box-shadow: 0 4px 15px rgba(150, 191, 19, 0.4);
	}

	.option-card.selected .option-check {
		opacity: 1;
		transform: scale(1);
	}

	.quote-divider {
		text-align: center;
		margin: 2.5rem 0;
		color: #555;
		font-size: 0.95rem;
		position: relative;
		padding: 0.8rem 0;
	}

	.quote-divider::before,
	.quote-divider::after {
		content: '';
		position: absolute;
		top: 50%;
		width: 32%;
		height: 1px;
		background: linear-gradient(to right, transparent, rgba(0, 0, 0, 0.1), transparent);
	}

	.quote-divider::before {
		left: 0;
	}

	.quote-divider::after {
		right: 0;
	}

	.quote-divider span {
		background: white;
		padding: 0 1rem;
		position: relative;
		z-index: 1;
	}

	.quote-divider strong {
		color: #1a1a1a;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.divider-subtitle {
		font-size: 0.8rem;
		color: #888;
		margin: 0.4rem 0 0 0;
	}

	.phone-input-group {
		display: flex;
		gap: 0.8rem;
		margin-bottom: 2.5rem;
	}

	.phone-input {
		flex: 1;
		padding: 1.1rem 1.3rem;
		border: 2px solid #e0e0e0;
		border-radius: 8px;
		font-size: 1rem;
		transition: all 0.3s ease;
		background: white;
		font-weight: 500;
	}

	.phone-input::placeholder {
		color: #999;
		font-weight: 400;
	}

	.phone-input:focus {
		outline: none;
		border-color: #1a1a1a;
		box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.05);
	}

	.btn-submit {
		padding: 1.1rem 2.5rem;
		background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
		color: white;
		font-weight: 700;
		border-radius: 8px;
		border: none;
		cursor: pointer;
		transition: all 0.3s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.8rem;
		text-transform: uppercase;
		letter-spacing: 0.8px;
		font-size: 0.9rem;
		box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
	}

	.btn-submit .submit-arrow {
		font-size: 1.4rem;
		transition: transform 0.3s ease;
	}

	.btn-submit:hover {
		background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
		transform: translateY(-2px);
		box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
	}

	.btn-submit:hover .submit-arrow {
		transform: translateX(3px);
	}

	.quote-footer {
		text-align: center;
		margin-top: 2.5rem;
		padding-top: 2rem;
		border-top: 2px solid #f0f0f0;
	}

	.call-label {
		color: #666;
		font-size: 0.85rem;
		margin-bottom: 0.8rem;
		text-transform: uppercase;
		letter-spacing: 1px;
		font-weight: 600;
	}

	.phone-number {
		color: #96bf13;
		font-size: 2rem;
		font-weight: 800;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 0.8rem;
		margin: 0.5rem 0 1rem 0;
		letter-spacing: 0.5px;
		transition: all 0.3s ease;
		text-decoration: none;
		position: relative;
	}

	.phone-number:hover {
		color: #82a510;
		transform: translateX(5px);
	}

	.phone-icon {
		width: 32px;
		height: 32px;
		flex-shrink: 0;
		display: block;
		stroke: #96bf13;
		animation: ring-subtle 3s ease-in-out infinite;
	}

	.phone-number:hover .phone-icon {
		stroke: #82a510;
	}

	@keyframes ring-subtle {
		0%, 100% {
			transform: rotate(0deg);
		}
		5%, 15% {
			transform: rotate(-8deg);
		}
		10%, 20% {
			transform: rotate(8deg);
		}
		25% {
			transform: rotate(0deg);
		}
	}

	.phone-number:hover {
		color: #2d2d2d;
		transform: scale(1.02);
	}

	.disclaimer-text {
		font-size: 0.8rem !important;
		color: #999 !important;
		margin-top: 0.8rem;
		font-weight: 400;
	}

	/* Final Form Styles */
	.success-icon {
		width: 80px;
		height: 80px;
		margin: 0 auto 1.5rem;
		background: rgba(150, 191, 19, 0.15);
		border: 3px solid #96bf13;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		animation: bounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
	}

	.success-icon svg {
		width: 40px;
		height: 40px;
		stroke: #96bf13;
		stroke-width: 2.5;
	}

	@keyframes bounceIn {
		0% {
			transform: scale(0);
			opacity: 0;
		}
		50% {
			transform: scale(1.2);
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.form-sublabel {
		font-size: 1.1rem;
		color: rgba(255, 255, 255, 0.8);
		margin-top: 1rem;
		font-weight: 400;
	}

	.final-form {
		max-width: 500px;
		margin: 0 auto;
	}

	.form-group {
		margin-bottom: 1.5rem;
		text-align: left;
	}

	.form-group label {
		display: block;
		color: rgba(255, 255, 255, 0.9);
		font-weight: 600;
		margin-bottom: 0.5rem;
		font-size: 0.95rem;
	}

	.form-input {
		width: 100%;
		padding: 1rem 1.2rem;
		border: 2px solid rgba(255, 255, 255, 0.2);
		border-radius: 10px;
		font-size: 1rem;
		transition: all 0.3s ease;
		background: rgba(255, 255, 255, 0.1);
		color: white;
		font-weight: 500;
		backdrop-filter: blur(10px);
	}

	.form-input-large {
		padding: 1.4rem 1.5rem;
		font-size: 1.15rem;
		text-align: center;
		letter-spacing: 0.5px;
		font-weight: 600;
	}

	.form-input::placeholder {
		color: rgba(255, 255, 255, 0.4);
		font-weight: 400;
	}

	.form-input:focus {
		outline: none;
		border-color: #96bf13;
		background: rgba(255, 255, 255, 0.15);
		box-shadow: 0 0 0 3px rgba(150, 191, 19, 0.2);
	}

	.privacy-notice {
		text-align: center;
		font-size: 0.85rem;
		color: rgba(255, 255, 255, 0.6);
		margin-top: 1.5rem;
		line-height: 1.5;
	}

	/* Message Screen */
	.message-screen {
		text-align: center;
		padding: 1.5rem 0;
		animation: fadeIn 0.4s ease-out;
	}

	.message-content {
		background: rgba(255, 255, 255, 0.05);
		border: 2px solid rgba(150, 191, 19, 0.3);
		border-radius: 15px;
		padding: 2rem 1.75rem;
		margin-bottom: 1.75rem;
		backdrop-filter: blur(10px);
	}

	.message-icon {
		font-size: 2.75rem;
		margin-bottom: 1.25rem;
		animation: bounceIn 0.6s ease-out;
	}

	.message-text {
		color: rgba(255, 255, 255, 0.9);
		font-size: 1.05rem;
		line-height: 1.5;
		margin: 0;
	}

	/* Form Screen */
	.form-screen {
		padding: 1.5rem 0;
		animation: fadeIn 0.4s ease-out;
	}

	@keyframes bounceIn {
		0% {
			opacity: 0;
			transform: scale(0.3);
		}
		50% {
			transform: scale(1.05);
		}
		100% {
			opacity: 1;
			transform: scale(1);
		}
	}

	.btn {
		padding: 1rem 2.5rem;
		font-size: 1rem;
		font-weight: 600;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.3s ease;
		border: none;
	}

	.btn-primary {
		background: #1a1a1a;
		color: white;
		border: 2px solid #1a1a1a;
	}

	.btn-primary:hover {
		background: #2d2d2d;
		border-color: #2d2d2d;
		transform: translateY(-2px);
		box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
	}

	.btn-secondary {
		background: transparent;
		color: white;
		border: 2px solid white;
	}

	.btn-secondary:hover {
		background: white;
		color: #1a1a1a;
	}

	/* Info Banner */
	.info-banner {
		background: white;
		padding: 3rem 0;
		text-align: center;
		border-bottom: 1px solid #e0e0e0;
	}

	.info-banner h2 {
		font-size: clamp(1.5rem, 3vw, 2rem);
		color: #1a1a1a;
		font-weight: 700;
		line-height: 1.4;
	}

	/* Stats Section */
	.stats {
		background: #000000;
		padding: 4rem 0;
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 3rem;
		text-align: center;
	}

	.stat-item {
		padding: 1rem;
	}

	.stat-value {
		font-size: 3rem;
		font-weight: 700;
		color: #FFFFFF;
		margin-bottom: 0.5rem;
		line-height: 1.1;
	}

	.stat-item:last-child .stat-value {
		font-size: 1.5rem;
		font-weight: 800;
		line-height: 1.3;
	}

	.stat-label {
		font-size: 1rem;
		color: rgba(255, 255, 255, 0.9);
		text-transform: uppercase;
		letter-spacing: 1px;
		line-height: 1.4;
	}

	/* Section Header */
	.section-header {
		text-align: center;
		margin-bottom: 4rem;
	}

	.section-header h2 {
		font-size: clamp(2rem, 4vw, 3rem);
		color: #1a1a1a;
		margin-bottom: 1rem;
	}

	.section-header p {
		font-size: 1.2rem;
		color: #666;
	}

	/* Panther Alarme Section */
	.panther-alarme {
		padding: 3rem 0 2rem;
		background: #ffffff;
	}

	.panther-alarme-header {
		text-align: center;
		margin-bottom: 1.5rem;
	}

	.panther-alarme-header h2 {
		font-size: 2.5rem;
		font-weight: 800;
		color: #1a1a1a;
		margin-bottom: 1rem;
		line-height: 1.2;
	}

	.panther-alarme-header p {
		font-size: 1rem;
		color: #666;
		line-height: 1.6;
		max-width: 800px;
		margin: 0 auto;
	}

	.panther-alarme-hero {
		margin: 0 auto 2rem;
		max-width: 650px;
	}

	.panther-products-image {
		width: 100%;
		height: auto;
		display: block;
		filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
	}

	.panther-alarme-feature-wrapper {
		position: relative;
		display: flex;
		align-items: center;
		gap: 2rem;
		margin-bottom: 2rem;
	}

	.panther-alarme-feature {
		display: grid;
		grid-template-columns: 320px 1fr;
		gap: 3rem;
		align-items: center;
		padding: 2rem 0;
		background: transparent;
		flex: 1;
		position: relative;
	}

	.panther-placa-slider {
		position: relative;
	}

	.placa-slider-container {
		position: relative;
		width: 100%;
		overflow: hidden;
		border-radius: 20px;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
		padding: 3rem 2rem;
		min-height: 320px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid rgba(255, 255, 255, 0.1);
		backdrop-filter: blur(10px);
	}

	.placa-slide {
		opacity: 0;
		transform: scale(0.9);
		transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
		            transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%) scale(0.9);
		pointer-events: none;
		width: 100%;
		padding: 2rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.placa-slide.active {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1);
		position: absolute;
		pointer-events: all;
	}

	.panther-placa-image {
		width: 100%;
		max-width: 220px;
		height: auto;
		display: block;
		filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.12));
		transition: transform 0.3s ease;
	}

	.panther-placa-image:hover {
		transform: scale(1.05);
	}

	.slider-arrow {
		width: 36px;
		height: 36px;
		border-radius: 50%;
		background: white;
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 10;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		flex-shrink: 0;
	}

	.slider-arrow:hover {
		background: #96bf13;
		transform: scale(1.1);
		box-shadow: 0 8px 24px rgba(150, 191, 19, 0.4);
	}

	.slider-arrow:hover svg {
		stroke: white;
	}

	.slider-arrow:active {
		transform: scale(0.95);
	}

	.slider-arrow svg {
		width: 16px;
		height: 16px;
		stroke: #1a1a1a;
		transition: stroke 0.3s ease;
	}

	.placa-slider-dots {
		display: flex;
		justify-content: center;
		gap: 6px;
		margin-top: 0;
	}

	.slider-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		border: none;
		background: #e0e0e0;
		cursor: pointer;
		transition: all 0.2s ease;
		padding: 0;
		position: relative;
	}

	.slider-dot::before {
		display: none;
	}

	.slider-dot.active {
		background: #96bf13;
		width: 18px;
		border-radius: 3px;
	}

	.slider-dot.active::before {
		display: none;
	}

	.slider-dot:hover {
		background: #82a510;
	}

	.panther-placa-container {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.panther-feature-content {
		position: relative;
	}

	.panther-feature-content h3 {
		font-size: 2.5rem;
		font-weight: 800;
		color: #1a1a1a;
		margin-bottom: 0.5rem;
		line-height: 1.2;
	}

	.panther-feature-content h4 {
		font-size: 1rem;
		font-weight: 600;
		color: #666;
		margin-bottom: 1.5rem;
		line-height: 1.4;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.panther-feature-content p {
		font-size: 1rem;
		color: #555;
		line-height: 1.7;
	}

	/* Defense Features Slider */
	.defense-features-slider {
		position: relative;
		padding: 1.5rem 0;
		max-width: 800px;
		margin: 0 auto;
	}

	.defense-slide-container {
		position: relative;
		min-height: 220px;
		overflow: hidden;
	}

	.defense-slide {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 220px;
		opacity: 0;
		transform: translateX(100px);
		transition: opacity 0.5s ease, transform 0.5s ease;
		pointer-events: none;
		display: flex;
		flex-direction: column;
		background: white;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
		border: 1px solid #e5e5e5;
	}

	.defense-slide.active {
		opacity: 1;
		transform: translateX(0);
		pointer-events: all;
		position: relative;
		border-color: #d0d0d0;
	}

	.defense-slide-header {
		background: #1a1a1a;
		padding: 0.5rem 1.5rem;
	}

	.defense-slide-header h4 {
		font-size: 0.7rem;
		font-weight: 600;
		color: #96bf13;
		text-transform: uppercase;
		letter-spacing: 0.1em;
		margin: 0;
	}

	.defense-slide-body {
		display: flex;
		flex-direction: row;
		flex: 1;
		align-items: stretch;
	}

	.defense-slide-image {
		display: flex;
		align-items: center;
		justify-content: center;
		background: white;
		padding: 1rem;
		width: 40%;
		flex-shrink: 0;
		overflow: hidden;
		border-right: 1px solid #e5e5e5;
	}

	.defense-slide-image img {
		max-width: 100%;
		max-height: 140px;
		object-fit: contain;
		display: block;
	}

	.defense-slide-image.multi-image {
		flex-direction: row;
		gap: 0.5rem;
		padding: 1rem;
	}

	.defense-slide-image.multi-image img {
		max-width: 30%;
		max-height: 120px;
		object-fit: contain;
	}

	.defense-slide-image.full-width {
		padding: 0;
		border-right: none;
	}

	.defense-slide-image.full-width img {
		max-width: 100%;
		max-height: none;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	.defense-slide-content {
		padding: 1rem 1.5rem;
		background: white;
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
	}

	.defense-slide-content.full-content {
		padding: 1.5rem 2rem;
		justify-content: center;
	}

	.defense-slide.no-image .defense-slide-body {
		background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	}

	.defense-slide-content h5 {
		font-size: 1.1rem;
		font-weight: 700;
		color: #1a1a1a;
		margin-bottom: 0.5rem;
		margin-top: 0;
		line-height: 1.2;
	}

	.feature-icon {
		display: none;
	}

	.defense-features-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
	}

	.defense-features-list li {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		font-size: 0.85rem;
		color: #555;
		line-height: 1.4;
	}

	.defense-features-list .check-icon {
		width: 12px;
		height: 12px;
		stroke: #96bf13;
		flex-shrink: 0;
	}

	.defense-slider-arrow {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background: white;
		border: 1.5px solid rgba(150, 191, 19, 0.2);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s ease;
		z-index: 10;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.defense-slider-arrow:hover {
		background: #96bf13;
		border-color: #96bf13;
		transform: translateY(-50%) scale(1.1);
	}

	.defense-slider-arrow:hover svg {
		stroke: white;
	}

	.defense-arrow-left {
		left: -50px;
	}

	.defense-arrow-right {
		right: -50px;
	}

	.defense-slider-arrow svg {
		width: 14px;
		height: 14px;
		stroke: #96bf13;
	}

	.defense-slider-dots {
		display: flex;
		justify-content: center;
		gap: 6px;
		margin-top: 0.75rem;
	}

	.defense-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: rgba(150, 191, 19, 0.3);
		border: none;
		cursor: pointer;
		transition: all 0.2s ease;
		padding: 0;
	}

	.defense-dot.active {
		background: #96bf13;
		width: 18px;
		height: 6px;
		border-radius: 3px;
	}

	.defense-dot:hover {
		background: #96bf13;
	}

	/* CTA Bar - Reusable Component */
	.cta-bar {
		background: linear-gradient(135deg, #96bf13 0%, #82a510 100%);
		padding: 0;
		position: relative;
		overflow: hidden;
	}

	.cta-bar::before {
		content: '';
		position: absolute;
		top: 0;
		left: -100%;
		width: 100%;
		height: 100%;
		background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
		animation: shimmer 3s infinite;
	}

	@keyframes shimmer {
		0%, 100% {
			left: -100%;
		}
		50% {
			left: 100%;
		}
	}

	.cta-bar-content {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 2rem 2rem;
		gap: 2rem;
		position: relative;
		z-index: 1;
	}

	.cta-bar-text h3 {
		font-size: 1.75rem;
		font-weight: 800;
		color: white;
		margin: 0 0 0.5rem 0;
		line-height: 1.2;
	}

	.cta-bar-discount {
		font-size: 1rem;
		color: rgba(255, 255, 255, 0.95);
		margin: 0;
		font-weight: 500;
	}

	.cta-bar-button {
		background: white;
		color: #1a1a1a;
		border: none;
		padding: 1rem 2.5rem;
		border-radius: 50px;
		font-size: 1rem;
		font-weight: 800;
		letter-spacing: 1px;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 0.75rem;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
		white-space: nowrap;
		text-transform: uppercase;
	}

	.cta-bar-button:hover {
		transform: translateY(-3px) scale(1.05);
		box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
		background: #f5f5f5;
	}

	.cta-bar-button:active {
		transform: translateY(-1px) scale(1.02);
	}

	.cta-bar-button svg {
		width: 24px;
		height: 24px;
		stroke: #96bf13;
		transition: transform 0.3s ease;
	}

	.cta-bar-button:hover svg {
		transform: translateX(5px);
	}

	/* Panther Segurança Section */
	.panther-seguranca {
		position: relative;
		padding: 0;
		min-height: 100vh;
		background-image: url('../panther-seguranca_coi_1.jpg');
		background-size: cover;
		background-position: center;
		background-attachment: fixed;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.panther-seguranca-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(10, 10, 10, 0.95) 100%);
		z-index: 1;
	}

	.panther-seguranca-content {
		position: relative;
		z-index: 2;
		width: 100%;
		max-width: 1400px;
		padding: 4rem 2rem;
		margin: 0 auto;
	}

	.panther-seguranca-brand {
		text-align: center;
		margin-bottom: 1rem;
		animation: fadeInDown 1s ease-out;
	}

	.panther-seguranca-logo {
		width: 200px;
		height: auto;
		margin-bottom: 2rem;
		filter: brightness(0) invert(1);
		animation: fadeIn 1.5s ease-out;
	}

	.panther-seguranca-title {
		font-size: 5rem;
		font-weight: 900;
		color: white;
		letter-spacing: 8px;
		text-transform: uppercase;
		margin: 0;
		text-shadow: 0 4px 30px rgba(0, 0, 0, 0.8);
	}

	/* Hexagon Stats Container */
	.stats-hexagon-container {
		max-width: 1100px;
		margin: 0 auto;
		padding: 1.5rem 0;
	}

	.hexagons-track {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 1rem;
	}

	.hexagon-stat {
		position: relative;
		aspect-ratio: 1;
		opacity: 0;
		animation: hexagonFadeIn 0.8s ease-out forwards;
		animation-delay: var(--delay);
	}

	/* Hide duplicate hexagons on desktop (only show first 8) */
	.hexagon-stat:nth-child(n+9) {
		display: none;
	}

	.hexagon-inner {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 1.5rem 1rem;
		background: linear-gradient(135deg,
			rgba(150, 191, 19, 0.85),
			rgba(130, 165, 16, 0.9),
			rgba(96, 191, 19, 0.8));
		backdrop-filter: blur(15px);
		border: 2px solid rgba(255, 255, 255, 0.8);
		clip-path: polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%);
		transition: all 0.4s ease;
		cursor: pointer;
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
	}

	.hexagon-stat:hover .hexagon-inner {
		background: linear-gradient(135deg,
			rgba(150, 191, 19, 1),
			rgba(130, 165, 16, 1),
			rgba(96, 191, 19, 0.95));
		border-color: rgba(255, 255, 255, 1);
		transform: scale(1.05);
		box-shadow: 0 12px 48px rgba(150, 191, 19, 0.6),
					0 0 30px rgba(255, 255, 255, 0.3);
	}

	.stat-number {
		font-size: 2.8rem;
		font-weight: 900;
		color: #ffffff;
		text-shadow: 0 0 20px rgba(150, 191, 19, 0.6),
					 0 0 40px rgba(150, 191, 19, 0.4);
		margin-bottom: 0.75rem;
		line-height: 1;
		letter-spacing: -1px;
	}

	.stat-label {
		font-size: 0.65rem;
		font-weight: 700;
		color: rgba(255, 255, 255, 0.95);
		text-align: center;
		text-transform: uppercase;
		letter-spacing: 1px;
		line-height: 1.2;
		text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
	}

	.stat-label-only {
		font-size: 1.1rem;
		font-weight: 900;
		color: #ffffff;
		text-align: center;
		text-transform: uppercase;
		letter-spacing: 1.5px;
		line-height: 1.3;
		text-shadow: 0 0 20px rgba(150, 191, 19, 0.6),
					 0 0 40px rgba(150, 191, 19, 0.4);
	}

	/* Keyframe Animations */
	@keyframes hexagonFadeIn {
		from {
			opacity: 0;
			transform: translateY(30px) scale(0.8);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	@keyframes hexagonFloat {
		0%, 100% {
			transform: translateY(0px);
		}
		50% {
			transform: translateY(-15px);
		}
	}

	@keyframes hexagonPulse {
		0%, 100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.03);
		}
	}

	.panther-about-text {
		max-width: 900px;
		margin: 0.1rem auto 3rem;
		padding: 0 2rem;
		text-align: center;
	}

	.about-title {
		color: #96bf13;
		font-size: 1.5rem;
		font-weight: 600;
		margin-bottom: 1.5rem;
		text-transform: uppercase;
		letter-spacing: 2px;
		text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
	}
	
	.about-end {
		color: #96bf13;
		font-size: 1rem;
		font-weight: 600;
		margin-bottom: 1.5rem;
		text-transform: uppercase;
		letter-spacing: 1px;
		text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
	}
	
	.panther-about-text p {
		color: white;
		font-size: 1.1rem;
		line-height: 1.8;
		text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
	}

	.feature-line {
		width: 100%;
		height: 3px;
		background: linear-gradient(90deg, #96bf13 0%, transparent 100%);
		box-shadow: 0 0 20px rgba(150, 191, 19, 0.6);
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes fadeInDown {
		from {
			opacity: 0;
			transform: translateY(-30px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	@keyframes slideInLeft {
		from {
			opacity: 0;
			transform: translateX(-50px);
		}
		to {
			opacity: 1;
			transform: translateX(0);
		}
	}

	.solution-link {
		color: #1a1a1a;
		font-weight: 600;
		display: inline-block;
	}

	.solution-link:hover {
		transform: translateX(5px);
	}

	/* Features Section */
	.features {
		padding: 6rem 0;
		background: #f8f8f8;
		color: #1a1a1a;
	}

	.features .section-header h2 {
		color: #1a1a1a;
	}

	.features .section-header p {
		color: #666;
	}

	.features-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: 2.5rem;
	}

	.feature-card {
		text-align: center;
		padding: 2rem;
	}

	.feature-icon {
		width: 64px;
		height: 64px;
		margin: 0 auto 1.5rem;
		color: #96bf13;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.feature-icon :global(svg) {
		width: 100%;
		height: 100%;
		stroke: #96bf13;
		stroke-width: 1.5;
	}

	.feature-card h3 {
		font-size: 1.3rem;
		margin-bottom: 1rem;
		color: #1a1a1a;
		font-weight: 700;
	}

	.feature-card p {
		color: #666;
		line-height: 1.6;
	}

	/* Testimonials Section */
	.testimonials {
		padding: 3rem 0;
		background: #f8f8f8;
	}

	.testimonials-container {
		position: relative;
		margin-top: 3rem;
		display: flex;
		align-items: center;
		gap: 2rem;
	}

	.testimonials-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 2.5rem;
		flex: 1;
	}

	.testimonial-arrow {
		width: 48px;
		height: 48px;
		background: white;
		border: 2px solid #e0e0e0;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s ease;
		flex-shrink: 0;
	}

	.testimonial-arrow:hover {
		background: #96bf13;
		border-color: #96bf13;
		transform: scale(1.1);
	}

	.testimonial-arrow svg {
		width: 24px;
		height: 24px;
		stroke: #666;
		transition: stroke 0.3s ease;
	}

	.testimonial-arrow:hover svg {
		stroke: white;
	}

	.testimonial-dots {
		display: flex;
		justify-content: center;
		gap: 6px;
		margin-top: 1rem;
	}

	.dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: #d0d0d0;
		border: none;
		cursor: pointer;
		transition: all 0.2s ease;
		padding: 0;
	}

	.dot:hover {
		background: #96bf13;
	}

	.dot.active {
		background: #96bf13;
		width: 18px;
		border-radius: 3px;
	}

	.testimonial-card {
		background: white;
		padding: 1.5rem;
		border-radius: 16px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
		transition: all 0.3s ease;
		display: flex;
		flex-direction: column;
		gap: 1rem;
		position: relative;
	}

	.testimonial-card:hover {
		transform: translateY(-8px);
		box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
	}

	.quote-icon {
		width: 48px;
		height: 48px;
		color: rgba(150, 191, 19, 0.15);
	}

	.quote-icon svg {
		width: 100%;
		height: 100%;
	}

	.testimonial-text {
		font-size: 0.9rem;
		line-height: 1.6;
		color: #333;
		font-style: italic;
		margin: 0;
		flex: 1;
	}

	.testimonial-rating {
		display: flex;
		gap: 0.3rem;
	}

	.star {
		color: #fbbf24;
		font-size: 1.3rem;
	}

	.testimonial-author {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding-top: 1rem;
		border-top: 2px solid #f0f0f0;
	}

	.author-avatar {
		width: 56px;
		height: 56px;
		border-radius: 50%;
		overflow: hidden;
		flex-shrink: 0;
	}

	.author-avatar img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.avatar-placeholder {
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, #96bf13, #82a510);
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-size: 1.5rem;
		font-weight: 700;
	}

	.author-info {
		flex: 1;
	}

	.author-name {
		font-size: 1.1rem;
		font-weight: 700;
		color: #1a1a1a;
		margin: 0 0 0.3rem 0;
	}

	.author-meta {
		font-size: 0.9rem;
		color: #666;
		margin: 0;
	}

	/* Trusted Companies Section */
	.trusted-companies {
		padding: 5rem 0;
		background: white;
		overflow: hidden;
	}

	.logos-container {
		margin-top: 3rem;
		position: relative;
		overflow: hidden;
		padding: 2rem 0;
		overflow-x: auto;
		overflow-y: hidden;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: none;
		-ms-overflow-style: none;
	}

	.logos-container::-webkit-scrollbar {
		display: none;
	}

	.logos-container::before,
	.logos-container::after {
		content: '';
		position: absolute;
		top: 0;
		width: 150px;
		height: 100%;
		z-index: 2;
		pointer-events: none;
	}

	.logos-container::before {
		left: 0;
		background: linear-gradient(to right, white, transparent);
	}

	.logos-container::after {
		right: 0;
		background: linear-gradient(to left, white, transparent);
	}

	.logos-track {
		display: flex;
		gap: 4rem;
		animation: scroll-logos 15s linear infinite;
		width: fit-content;
	}

	.logos-track:hover {
		animation-play-state: paused;
	}

	@keyframes scroll-logos {
		0% {
			transform: translateX(0);
		}
		100% {
			transform: translateX(-50%);
		}
	}

	.logo-item {
		flex-shrink: 0;
		width: 180px;
		height: 100px;
		display: flex;
		align-items: center;
		justify-content: center;
		filter: grayscale(100%) opacity(0.6);
		transition: all 0.3s ease;
	}

	.logo-item:hover {
		filter: grayscale(0%) opacity(1);
		transform: scale(1.05);
	}

	.logo-item img {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}

	.logo-placeholder {
		width: 100%;
		height: 100%;
		background: #f0f0f0;
		border-radius: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-weight: 600;
		color: #999;
		font-size: 0.9rem;
		text-align: center;
		padding: 1rem;
		border: 2px dashed #ddd;
	}

	/* Comparison Section */
	.comparison {
		padding: 6rem 0;
		background: #f8f8f8;
	}

	.comparison-table {
		max-width: 900px;
		margin: 3rem auto 0;
		background: white;
		border-radius: 20px;
		overflow: hidden;
		box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	}

	.comparison-header {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr;
		background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
		color: white;
		padding: 0.75rem 1.5rem;
		font-weight: 700;
		align-items: center;
	}

	.header-feature {
		font-size: 1.1rem;
	}

	.header-company {
		text-align: center;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.5rem;
		font-size: 1rem;
	}

	.panther-header {
		color: #96bf13;
	}

	.competitors-header {
		color: rgba(255, 255, 255, 0.6);
	}

	.comparison-logo {
		height: 40px;
		width: auto;
		filter: brightness(0) invert(1);
	}

	.comparison-body {
		background: white;
	}

	.comparison-row {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr;
		padding: 0.5rem 1.5rem;
		align-items: center;
		border-bottom: 1px solid #f0f0f0;
		transition: background 0.2s ease;
	}

	.comparison-row:hover {
		background: #fafafa;
	}

	.comparison-row.even {
		background: #f8f8f8;
	}

	.comparison-row.even:hover {
		background: #f0f0f0;
	}

	.feature-name {
		font-size: 0.9rem;
		color: #333;
		font-weight: 500;
	}

	.feature-value {
		text-align: center;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.check-icon {
		width: 28px;
		height: 28px;
		stroke: #96bf13;
		stroke-width: 3;
	}

	.cross-icon {
		width: 28px;
		height: 28px;
		stroke: #e74c3c;
		stroke-width: 2;
	}

	.panther-value .check-icon {
		background: rgba(150, 191, 19, 0.1);
		border-radius: 50%;
		padding: 0.3rem;
	}

	.comparison-footer {
		padding: 2.5rem;
		background: linear-gradient(to bottom, #f8f8f8 0%, #ffffff 100%);
		display: flex;
		justify-content: center;
	}

	.btn-comparison-cta {
		padding: 1.2rem 3rem;
		background: #96bf13;
		color: #1a1a1a;
		font-size: 1.1rem;
		font-weight: 700;
		border-radius: 10px;
		border: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 1rem;
		transition: all 0.3s ease;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		box-shadow: 0 4px 15px rgba(150, 191, 19, 0.3);
	}

	.btn-comparison-cta:hover {
		background: #82a510;
		transform: translateY(-3px);
		box-shadow: 0 8px 25px rgba(150, 191, 19, 0.4);
	}

	.btn-comparison-cta .arrow {
		font-size: 1.5rem;
		transition: transform 0.3s ease;
	}

	.btn-comparison-cta:hover .arrow {
		transform: translateX(5px);
	}

	/* About Section */
	.about {
		padding: 6rem 0;
		background: #f8f8f8;
	}

	.about-content {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 4rem;
		align-items: center;
	}

	.about-image {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.image-placeholder {
		width: 100%;
		max-width: 400px;
		aspect-ratio: 1;
		background: linear-gradient(135deg, #f8f8f8, #e0e0e0);
		border-radius: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 8rem;
	}

	.about-text h2 {
		font-size: clamp(2rem, 4vw, 2.5rem);
		color: #1a1a1a;
		margin-bottom: 1.5rem;
		line-height: 1.3;
	}

	.about-text p {
		color: #666;
		line-height: 1.8;
		margin-bottom: 1.5rem;
		font-size: 1.1rem;
	}

	/* CTA Section */
	.cta-section {
		padding: 6rem 0;
		background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
		color: white;
		text-align: center;
	}

	.cta-content h2 {
		font-size: clamp(2rem, 4vw, 3rem);
		margin-bottom: 1rem;
	}

	.cta-content p {
		font-size: 1.2rem;
		margin-bottom: 2.5rem;
		opacity: 0.9;
	}

	.cta-buttons {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	.btn-primary-white {
		background: white;
		color: #1a1a1a;
		border: 2px solid white;
	}

	.btn-primary-white:hover {
		background: transparent;
		color: white;
		border-color: white;
		transform: translateY(-2px);
		box-shadow: 0 10px 25px rgba(255, 255, 255, 0.2);
	}

	.btn-secondary-white {
		background: transparent;
		color: white;
		border: 2px solid white;
	}

	.btn-secondary-white:hover {
		background: white;
		color: #1a1a1a;
	}

	/* Footer */
	.footer {
		background: #1a1a1a;
		color: white;
		padding: 4rem 0 0;
	}

	.footer-content {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 3rem;
		margin-bottom: 3rem;
	}

	.footer-section:first-child {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}

	.footer-logo {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 1rem;
	}

	.footer-logo-img {
		height: 70px;
		width: auto;
	}

	.footer-logo-white {
		filter: brightness(0) invert(1);
	}

	.footer-section:first-child p {
		font-size: 1.1rem;
		font-weight: 600;
		letter-spacing: 0.5px;
		margin: 0;
	}

	.footer-section h4 {
		color: #ffffff;
		margin-bottom: 1rem;
		font-size: 1.1rem;
		font-weight: 600;
	}

	.footer-section ul {
		list-style: none;
	}

	.footer-section li {
		margin-bottom: 0.5rem;
	}

	.footer-section a {
		color: #e0e0e0;
		opacity: 0.8;
	}

	.footer-section a:hover {
		color: #ffffff;
		opacity: 1;
	}

	.footer-section p {
		color: #e0e0e0;
		opacity: 0.8;
		line-height: 1.6;
	}

	.footer-social {
		display: flex;
		gap: 1rem;
		margin-top: 1rem;
		justify-content: center;
	}

	.footer-social a {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.1);
		color: white;
		transition: all 0.3s ease;
	}

	.footer-social a:hover {
		background: #96bf13;
		transform: translateY(-3px);
	}

	.footer-address {
		margin-bottom: 1.5rem;
	}

	.footer-address p {
		margin: 0.3rem 0;
		font-size: 0.9rem;
	}

	.footer-address strong {
		color: #96bf13;
		display: block;
		margin-bottom: 0.5rem;
	}

	.footer-bottom {
		border-top: 1px solid rgba(255, 255, 255, 0.1);
		padding: 2rem 0;
		text-align: center;
		color: #999;
	}

	.footer-legal {
		font-size: 0.85rem;
		margin-top: 0.5rem;
		opacity: 0.7;
	}

	/* Back to Top Button */
	.back-to-top {
		position: fixed;
		bottom: 2rem;
		right: 2rem;
		width: 52px;
		height: 52px;
		background: rgba(150, 191, 19, 0.95);
		backdrop-filter: blur(10px);
		color: white;
		border: 2px solid rgba(255, 255, 255, 0.2);
		border-radius: 12px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		z-index: 999;
		animation: fadeInUp 0.4s ease;
	}

	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(30px) scale(0.9);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	.back-to-top:hover {
		background: #96bf13;
		transform: translateY(-6px) scale(1.05);
		box-shadow: 0 12px 40px rgba(150, 191, 19, 0.4);
		border-color: rgba(255, 255, 255, 0.4);
	}

	.back-to-top:active {
		transform: translateY(-2px) scale(0.98);
	}

	.back-to-top svg {
		width: 26px;
		height: 26px;
		stroke: white;
		stroke-width: 2.5;
	}

	/* Custom Confirmation Modal */
	.confirmation-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.75);
		backdrop-filter: blur(8px);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10000;
		animation: fadeIn 0.3s ease;
		padding: 1rem;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	.confirmation-modal {
		background: #1a1a1a;
		border-radius: 24px;
		padding: 0;
		max-width: 420px;
		width: 100%;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
		animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
		overflow: hidden;
		border: 2px solid rgba(150, 191, 19, 0.3);
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(30px) scale(0.95);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}

	.confirmation-modal.error {
		border-color: rgba(220, 53, 69, 0.4);
	}

	.confirmation-header {
		background: linear-gradient(135deg, #96bf13 0%, #82a510 100%);
		padding: 2rem;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.confirmation-header.error {
		background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
	}

	.confirmation-icon {
		width: 56px;
		height: 56px;
		stroke: #1a1a1a;
		stroke-width: 2.5;
		filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
	}

	.confirmation-content {
		padding: 2rem 2rem 1.5rem;
		text-align: center;
	}

	.confirmation-title {
		color: #96bf13;
		font-size: 1.8rem;
		font-weight: 700;
		margin: 0 0 1rem 0;
		letter-spacing: 0.5px;
	}

	.confirmation-title.error {
		color: #dc3545;
	}

	.confirmation-message {
		color: #ffffff;
		font-size: 1.05rem;
		line-height: 1.6;
		margin: 0;
		font-weight: 400;
		opacity: 0.95;
	}

	.confirmation-buttons {
		display: flex;
		gap: 1rem;
		padding: 0 2rem 2rem;
	}

	.confirmation-button {
		padding: 1rem 1.5rem;
		border: none;
		border-radius: 12px;
		font-size: 1rem;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.3s ease;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.confirmation-buttons .confirmation-button {
		flex: 1;
	}

	.confirmation-button-primary {
		background: #96bf13;
		color: #1a1a1a;
	}

	.confirmation-button-primary:hover {
		background: #82a510;
		transform: translateY(-2px);
		box-shadow: 0 8px 24px rgba(150, 191, 19, 0.4);
	}

	.confirmation-button-secondary {
		background: transparent;
		color: #ffffff;
		border: 2px solid rgba(255, 255, 255, 0.3);
	}

	.confirmation-button-secondary:hover {
		background: rgba(255, 255, 255, 0.1);
		border-color: rgba(255, 255, 255, 0.5);
		transform: translateY(-2px);
	}

	.confirmation-button:active {
		transform: translateY(0);
	}

	.confirmation-link {
		display: block;
		text-align: center;
		padding: 0.75rem 1.5rem;
		margin: 0.5rem 2rem 1.5rem;
		color: rgba(255, 255, 255, 0.7);
		text-decoration: none;
		font-size: 0.9rem;
		font-weight: 500;
		transition: all 0.3s ease;
		border-bottom: 1px solid rgba(255, 255, 255, 0.2);
	}

	.confirmation-link:hover {
		color: #96bf13;
		border-bottom-color: #96bf13;
	}

	/* Single button layout (for validation messages) */
	.confirmation-modal .confirmation-button:only-of-type {
		flex: none;
		width: calc(100% - 4rem);
		margin: 0 2rem 2rem;
		display: block;
	}

	/* Responsive */
	@media (max-width: 968px) {
		.hero-container {
			grid-template-columns: 1fr;
			gap: 2rem;
		}

		.quote-card {
			max-width: 500px;
			margin: 0 auto;
		}

		.nav-cta-section {
			flex-direction: column;
			gap: 1rem;
			align-items: flex-end;
		}

		.nav-promo-text {
			flex-direction: column;
			gap: 0.5rem;
			align-items: flex-end;
		}

		.nav-cta-buttons {
			flex-direction: column;
			width: 100%;
		}

		.nav-btn-call,
		.nav-btn-quote {
			width: 100%;
			justify-content: center;
		}
	}

	@media (max-width: 768px) {
		.mobile-menu-btn {
			display: flex;
		}

		.mobile-menu-btn.active {
			display: none;
		}

		.mobile-menu-close {
			display: none;
		}

		.nav-links.active .mobile-menu-close {
			display: block;
		}

		.mobile-menu-overlay {
			display: none;
		}

		.mobile-menu-overlay.active {
			display: block;
		}

		.nav-links {
			position: fixed;
			top: 0;
			right: -100%;
			width: 75%;
			max-width: 300px;
			height: 100vh;
			background: rgba(255, 255, 255, 0.98);
			backdrop-filter: blur(10px);
			flex-direction: column;
			justify-content: flex-start;
			padding: 5rem 2rem 2rem;
			transition: right 0.3s ease;
			gap: 1.5rem;
			box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
			z-index: 999;
			overflow-y: auto;
		}

		.nav-links.active {
			right: 0;
		}

		.navbar.scrolled .nav-links {
			background: rgba(26, 26, 26, 0.98);
		}

		.navbar.scrolled .nav-links a {
			color: white;
		}

		.navbar.scrolled .nav-links a::after {
			background: #96bf13;
		}

		.navbar.scrolled .nav-phone {
			color: #96bf13 !important;
		}

		.navbar.scrolled .nav-phone-label {
			color: rgba(255, 255, 255, 0.7);
		}

		.navbar.scrolled .site-link-text {
			color: white;
		}

		.navbar.scrolled .site-link-label {
			color: rgba(255, 255, 255, 0.7);
		}

		.navbar.scrolled .site-link-url {
			color: #96bf13;
		}

		.navbar.scrolled .site-icon {
			stroke: white;
		}

		.navbar.scrolled .mobile-menu-quote-btn-container {
			border-top: 1px solid rgba(255, 255, 255, 0.2);
		}

		.navbar.scrolled .mobile-menu-quote-btn {
			background: #96bf13;
			color: #1a1a1a;
		}

		.navbar.scrolled .mobile-menu-quote-btn:hover {
			background: #82a510;
		}

		.navbar.scrolled .mobile-menu-close {
			color: white;
		}

		.navbar.scrolled .mobile-menu-close svg {
			stroke: white;
		}

		.navbar.scrolled .mobile-menu-close:hover {
			color: #96bf13;
		}

		.navbar.scrolled .mobile-menu-close:hover svg {
			stroke: #96bf13;
		}

		.nav-links li {
			width: 100%;
			text-align: left;
		}

		.navbar.scrolled .nav-cta-section {
			width: 100%;
		}

		.promo-message {
			font-size: 0.85rem;
			text-align: right;
		}

		.promo-badge {
			font-size: 0.75rem;
			padding: 0.3rem 0.6rem;
		}

		.hero {
			padding: 6rem 0 0;
			min-height: 100vh;
		}

		.hero-container {
			gap: 2rem;
			padding: 0;
		}

		.hero-content {
			padding: 0 1.5rem;
		}

		.quote-card {
			padding: 1.5rem !important;
			height: auto !important;
			max-width: calc(100% - 2rem) !important;
			width: calc(100% - 2rem) !important;
			margin: 0 1rem !important;
			box-sizing: border-box;
			background: white !important;
			box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
			border-radius: 24px !important;
			border: 1px solid rgba(0, 0, 0, 0.05) !important;
			position: relative;
			left: 0;
			right: 0;
			margin-left: 1rem !important;
			margin-right: 1rem !important;
		}

		.quote-screen {
			padding: 2rem 1.5rem;
		}

		.stats-grid {
			grid-template-columns: repeat(2, 1fr);
			gap: 2rem;
		}

		.stat-value {
			font-size: 2.5rem;
		}

		.stat-item:last-child .stat-value {
			font-size: 1.3rem;
		}

		.stat-label {
			font-size: 0.95rem;
		}

		.about-content {
			grid-template-columns: 1fr;
		}

		.about-image {
			order: -1;
		}

		.solutions-grid {
			grid-template-columns: 1fr;
		}

		.features-grid {
			grid-template-columns: 1fr;
		}

		/* Trusted Companies - Mobile optimizations */
		.logos-container {
			padding: 1rem 0;
			margin-top: 2rem;
		}

		.logos-container::before,
		.logos-container::after {
			width: 50px;
		}

		.logos-track {
			gap: 2rem;
			animation: scroll-logos 12s linear infinite;
		}

		.logo-item {
			width: 120px;
			height: 70px;
		}
	}

	@media (max-width: 968px) {
		.testimonials-container {
			flex-direction: column;
		}

		.testimonials-grid {
			grid-template-columns: 1fr;
		}

		.testimonial-arrow {
			display: none;
		}
	}

	@media (max-width: 768px) {
		.container {
			padding: 0 1.5rem;
		}

		/* General text improvements for tablet */
		h2 {
			font-size: clamp(1.5rem, 5vw, 2rem);
		}

		h3 {
			font-size: clamp(1.2rem, 4vw, 1.5rem);
		}

		.cta-bar-content {
			flex-direction: column;
			text-align: center;
			padding: 2rem 1.5rem;
			gap: 1.5rem;
		}

		.cta-bar-text h3 {
			font-size: 1.3rem;
		}

		.cta-bar-discount {
			font-size: 0.9rem;
		}

		.cta-bar-button {
			width: 100%;
			justify-content: center;
			padding: 1.2rem 2rem;
		}

		.panther-alarme {
			padding: 3rem 0 2rem;
		}

		.panther-alarme-header {
			margin-bottom: 2rem;
		}

		.panther-alarme-header h2 {
			font-size: 1.75rem;
		}

		.panther-alarme-header p {
			font-size: 0.95rem;
		}

		.panther-alarme-hero {
			margin-bottom: 2rem;
		}

		.panther-alarme-feature-wrapper {
			gap: 1rem;
			margin-bottom: 1.5rem;
		}

		.panther-alarme-feature {
			grid-template-columns: 1fr;
			gap: 2rem;
			padding: 2rem 1.5rem;
		}

		.slider-arrow {
			width: 32px;
			height: 32px;
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
		}

		.slider-arrow svg {
			width: 16px;
			height: 16px;
		}

		.slider-arrow:hover {
			transform: scale(1.05);
		}

		.placa-slider-container {
			padding: 1.5rem;
			min-height: 240px;
		}

		.placa-slide {
			padding: 1.5rem;
		}

		.panther-placa-image {
			max-width: 180px;
		}

		.panther-feature-content {
			text-align: center;
		}

		.panther-feature-content h3 {
			font-size: 1.5rem;
		}

		.panther-feature-content h4 {
			font-size: 1rem;
		}

		.panther-feature-content h5 {
			text-align: center;
		}

		.panther-feature-content p {
			font-size: 0.95rem;
			text-align: center;
		}

		/* Defense Features Slider - Mobile */
		.defense-features-slider {
			padding: 1rem 0;
		}

		.defense-slide {
			height: auto;
		}

		.defense-slide-body {
			flex-direction: column;
		}

		.defense-slide-container {
			min-height: 350px;
		}

		.defense-slide-image {
			width: 100%;
			height: 150px;
			padding: 1rem;
		}

		.defense-slide-image.multi-image {
			height: auto;
			min-height: 100px;
		}

		.defense-slide-image.multi-image img {
			max-width: 30%;
			max-height: 80px;
		}

		.defense-slide-content {
			padding: 1rem;
			text-align: center;
		}

		.defense-slide-content h5 {
			font-size: 1rem;
			text-align: center;
		}

		.defense-features-list li {
			font-size: 0.8rem;
			text-align: left;
		}

		.defense-slider-arrow {
			width: 28px;
			height: 28px;
		}

		.defense-arrow-left {
			left: 5px;
		}

		.defense-arrow-right {
			right: 5px;
		}

		.defense-slider-arrow svg {
			width: 20px;
			height: 20px;
		}

		.defense-slider-dots {
			margin-top: 0.5rem !important;
			gap: 5px !important;
			height: auto !important;
			min-height: 0 !important;
		}

		.defense-dot {
			width: 6px !important;
			height: 6px !important;
			min-height: 6px !important;
			max-height: 6px !important;
			background: rgba(150, 191, 19, 0.4) !important;
			padding: 0 !important;
			line-height: 0 !important;
		}

		.defense-dot.active {
			width: 16px !important;
			height: 6px !important;
			min-height: 6px !important;
			max-height: 6px !important;
			border-radius: 3px !important;
			background: #96bf13 !important;
		}

		.defense-dot:hover {
			transform: none !important;
		}

		.feature-icon {
			display: none;
		}

		.panther-seguranca {
			min-height: auto;
			padding: 4rem 0;
		}

		.panther-seguranca-logo {
			width: 120px;
		}

		.panther-seguranca-title {
			font-size: 2.5rem;
			letter-spacing: 4px;
		}

		.panther-seguranca-brand {
			margin-bottom: 0.5rem;
		}

		.stats-hexagon-container {
			overflow: hidden;
			padding: 2rem 0;
		}

		.hexagons-track {
			display: flex;
			gap: 1.5rem;
			animation: scroll-hexagons 20s linear infinite;
			width: fit-content;
		}

		.hexagons-track:hover {
			animation-play-state: paused;
		}

		@keyframes scroll-hexagons {
			0% {
				transform: translateX(0);
			}
			100% {
				transform: translateX(-50%);
			}
		}

		.hexagon-stat {
			min-width: 130px;
			max-width: 130px;
			flex-shrink: 0;
		}

		/* Show duplicate hexagons on mobile for infinite scroll */
		.hexagon-stat:nth-child(n+9) {
			display: block;
		}

		.hexagon-inner {
			padding: 1rem 0.5rem;
		}

		.stat-number {
			font-size: 1.3rem;
			margin-bottom: 0.2rem;
		}

		.stat-label {
			font-size: 0.58rem !important;
			font-weight: 700 !important;
			letter-spacing: -0.3px !important;
			line-height: 1.1 !important;
			text-align: center !important;
			padding: 0 0.1rem !important;
			text-transform: uppercase !important;
		}

		.stat-label-only {
			font-size: 0.75rem;
			letter-spacing: 0.4px;
			line-height: 1.05;
			font-weight: 800;
		}

		.panther-seguranca-content {
			padding: 3rem 1.5rem;
		}

		.comparison-header,
		.comparison-row {
			grid-template-columns: 1fr;
			text-align: center;
		}

		.comparison-header {
			padding: 1rem;
		}

		.header-feature {
			display: none;
		}

		.feature-name {
			margin-bottom: 1rem;
			font-weight: 600;
		}

		.feature-value {
			display: inline-flex;
			margin: 0 1rem;
		}

		.comparison-row {
			padding: 1.5rem 1rem;
		}

		.comparison-logo {
			height: 30px;
		}

		.comparison-footer {
			padding: 1.5rem;
		}

		.btn-comparison-cta {
			width: 100%;
			padding: 1rem 2rem;
			font-size: 0.95rem;
		}

		.options-grid,
		.options-grid.grid-4 {
			grid-template-columns: repeat(2, 1fr);
			gap: 0.75rem;
		}

		.option-card {
			min-height: 110px;
			padding: 0.875rem 1rem;
			flex-direction: column;
			text-align: center;
			justify-content: center;
			align-items: center;
			color: #1a1a1a;
		}

		button.option-card {
			width: 100%;
			color: #1a1a1a;
		}

		.option-icon {
			font-size: 2.5rem;
			margin-bottom: 0.5rem;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.option-icon :global(svg) {
			width: 2.5rem;
			height: 2.5rem;
			stroke: #1a1a1a;
		}

		.option-content {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
		}

		.option-content h3 {
			font-size: 0.975rem;
			margin-bottom: 0.2rem;
			color: #1a1a1a;
			line-height: 1.3;
		}

		.option-content p {
			font-size: 0.75rem;
			opacity: 0.8;
			margin: 0;
			color: #1a1a1a;
			line-height: 1.3;
		}

		.progress-bar {
			background: rgba(0, 0, 0, 0.1);
			margin-bottom: 2rem;
		}

		.message-content {
			background: rgba(0, 0, 0, 0.03);
			border: 2px solid rgba(150, 191, 19, 0.3);
		}

		.message-text {
			color: #1a1a1a;
		}

		.form-group label {
			color: #1a1a1a;
		}

		.form-input {
			background: rgba(0, 0, 0, 0.03);
			border: 2px solid rgba(0, 0, 0, 0.15);
			color: #1a1a1a;
		}

		.form-input::placeholder {
			color: rgba(0, 0, 0, 0.4);
		}

		.form-input:focus {
			background: rgba(0, 0, 0, 0.05);
			border-color: #96bf13;
		}

		.form-sublabel {
			color: rgba(0, 0, 0, 0.6);
		}

		.privacy-notice {
			color: rgba(0, 0, 0, 0.5);
		}

		.voltar-button-wrapper {
			padding-top: 1.5rem;
			background: transparent;
		}

		.btn-back {
			background: rgba(0, 0, 0, 0.05);
			color: #1a1a1a;
			border: 1px solid rgba(0, 0, 0, 0.1);
			margin-bottom: 0;
			padding: 0.75rem 1.5rem;
			font-size: 0.95rem;
		}

		.btn-back:hover {
			background: rgba(0, 0, 0, 0.1);
			color: #1a1a1a;
			border-color: rgba(0, 0, 0, 0.2);
		}

		.form-label {
			font-size: 1.5rem;
			color: #1a1a1a;
		}

		.question-content {
			height: 550px;
			padding: 1.5rem;
		}

		.question-title {
			font-size: 1.5rem;
			line-height: 1.3;
			margin-bottom: 1.5rem;
		}

		/* Confirmation Modal Mobile */
		.confirmation-modal {
			max-width: 90%;
			border-radius: 20px;
		}

		.confirmation-header {
			padding: 1.5rem;
		}

		.confirmation-icon {
			width: 48px;
			height: 48px;
		}

		.confirmation-content {
			padding: 1.5rem 1.5rem 1rem;
		}

		.confirmation-title {
			font-size: 1.5rem;
		}

		.confirmation-message {
			font-size: 0.95rem;
			line-height: 1.5;
		}

		.confirmation-buttons {
			flex-direction: column;
			padding: 0 1.5rem 1.5rem;
			gap: 0.75rem;
		}

		.confirmation-button {
			padding: 0.875rem 1.5rem;
			font-size: 0.95rem;
		}

		.confirmation-link {
			margin: 0.5rem 1.5rem 1.5rem;
			font-size: 0.85rem;
		}

		/* Single button on mobile */
		.confirmation-modal .confirmation-button:only-of-type {
			flex: none;
			width: calc(100% - 3rem);
			margin: 0 1.5rem 1.5rem;
			display: block;
		}
	}

	@media (max-width: 480px) {
		.container {
			padding: 0 1rem;
		}

		/* Navbar Mobile Simplification */
		.navbar {
			padding: 0.75rem 0;
		}

		.navbar.scrolled {
			padding: 0.5rem 0;
		}

		.nav-container {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 0 1rem;
		}

		.mobile-call-btn {
			display: flex;
		}

		.logo {
			position: absolute;
			left: 50%;
			transform: translateX(-50%);
		}

		.mobile-menu-btn {
			display: flex !important;
			z-index: 1001;
			position: relative;
			margin-left: auto;
		}

		.mobile-menu-btn.active {
			display: none !important;
		}

		.navbar.scrolled .mobile-menu-btn {
			display: flex !important;
		}

		.navbar.scrolled .mobile-menu-btn.active {
			display: none !important;
		}

		.hamburger,
		.hamburger::before,
		.hamburger::after {
			background: #1a1a1a;
		}

		.navbar.scrolled .hamburger,
		.navbar.scrolled .hamburger::before,
		.navbar.scrolled .hamburger::after {
			background: white;
		}

		.navbar.scrolled .menu-text {
			color: white;
		}

		.navbar.scrolled .nav-cta-section {
			display: none !important;
		}

		.logo-img {
			height: 60px;
		}

		.navbar.scrolled .logo-img {
			height: 55px;
		}

		/* CTA Bar Mobile Improvements */
		.cta-bar {
			padding: 0;
		}

		.cta-bar-content {
			flex-direction: column;
			text-align: center;
			padding: 2rem 1.25rem;
			gap: 1.25rem;
		}

		.cta-bar-text {
			max-width: 100%;
		}

		.cta-bar-text h3 {
			font-size: 1.4rem;
			line-height: 1.25;
			margin-bottom: 0.5rem;
		}

		.cta-bar-discount {
			font-size: 0.9rem;
			line-height: 1.4;
		}

		.cta-bar-button {
			width: 100%;
			padding: 1rem 1.5rem;
			font-size: 0.95rem;
			border-radius: 50px;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 0.5rem;
		}

		.cta-bar-button svg {
			width: 20px;
			height: 20px;
		}

		.hero {
			padding: 5rem 0 0;
			min-height: 100vh;
		}

		.hero-container {
			gap: 1rem;
			padding: 0;
			display: flex;
			flex-direction: column;
		}

		.stats {
			padding: 3rem 0 !important;
			margin-top: 0 !important;
		}

		/* Hexagon Stats Mobile */
		.stats-hexagon-container {
			overflow: hidden;
			padding: 1.5rem 0;
		}

		.hexagons-track {
			display: flex;
			gap: 1rem;
			animation: scroll-hexagons 18s linear infinite;
			width: fit-content;
		}

		.hexagons-track:hover {
			animation-play-state: paused;
		}

		@keyframes scroll-hexagons {
			0% {
				transform: translateX(0);
			}
			100% {
				transform: translateX(-50%);
			}
		}

		.hexagon-stat {
			min-width: 130px;
			max-width: 130px;
			flex-shrink: 0;
		}

		/* Show duplicate hexagons on mobile for infinite scroll */
		.hexagon-stat:nth-child(n+9) {
			display: block;
		}

		.hexagon-inner {
			padding: 1rem 0.5rem;
		}

		.stat-number {
			font-size: 1.3rem;
			margin-bottom: 0.2rem;
		}

		.stat-label {
			font-size: 0.58rem !important;
			font-weight: 700 !important;
			letter-spacing: -0.3px !important;
			line-height: 1.1 !important;
			text-align: center !important;
			padding: 0 0.1rem !important;
			text-transform: uppercase !important;
		}

		.stat-label-only {
			font-size: 0.75rem;
			letter-spacing: 0.4px;
			line-height: 1.05;
			font-weight: 800;
		}

		.panther-seguranca-content {
			padding: 2rem 1rem;
		}

		.desktop-hero-content {
			display: none;
		}

		.mobile-logo-container {
			display: block;
			text-align: center;
			margin: 1.5rem 0 1.5rem;
			order: 0;
		}

		.mobile-hero-logo {
			height: 140px;
			filter: brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4));
		}

		.logo {
			display: none !important;
		}

		.navbar.scrolled .logo {
			display: block !important;
		}

		.navbar.scrolled .logo-img {
			height: 50px !important;
		}

		.mobile-hero-content-section {
			display: block;
			background: #000000;
			padding: 2rem 0;
			margin: 0;
		}

		.mobile-hero-content-section .hero-content {
			padding: 0 1rem;
			margin: 0;
		}

		.mobile-hero-content-section .hero-title {
			color: #ffffff;
		}

		.mobile-hero-content-section .hero-subtitle {
			color: rgba(255, 255, 255, 0.9);
		}

		.mobile-hero-content-section .hero-discount {
			color: rgba(255, 255, 255, 0.8);
		}

		.mobile-hero-content-section .hero-title {
			font-size: 1.6rem;
			line-height: 1.15;
			margin-bottom: 0.5rem;
			text-shadow: none;
		}

		.mobile-hero-content-section .hero-subtitle {
			font-size: 0.95rem;
			max-width: 100%;
			margin-bottom: 0.5rem;
			line-height: 1.5;
			text-shadow: none;
			font-weight: 800;
		}

		.mobile-hero-content-section .hero-discount {
			font-size: 0.8rem;
			margin-bottom: 0.5rem;
			text-shadow: none;
		}

		.hero-tagline {
			display: none;
		}

		.quote-card {
			padding: 1rem !important;
			height: auto !important;
			max-width: calc(100% - 1.5rem) !important;
			width: calc(100% - 1.5rem) !important;
			margin: 0 0.75rem !important;
			box-sizing: border-box;
			background: white !important;
			box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
			border-radius: 24px !important;
			border: 1px solid rgba(0, 0, 0, 0.05) !important;
			position: relative;
			left: 0;
			right: 0;
			order: 1;
		}

		.quote-screen {
			display: flex;
			flex-direction: column;
			gap: 0.5rem;
			background: transparent;
			border-radius: 0;
			padding: 0.75rem 1.25rem;
			box-shadow: none;
			min-height: auto;
			justify-content: flex-start;
		}

		/* Modern unified design */
		.quote-header {
			text-align: center;
			padding: 0;
			background: transparent;
			box-shadow: none;
			border-radius: 0;
			margin-top: 1rem;
		}

		.quote-header h3 {
			font-size: 1.5rem;
			margin-bottom: 0.5rem;
			line-height: 1.2;
			color: #1a1a1a;
		}

		.quote-header p {
			font-size: 0.85rem;
			margin-bottom: 0;
			line-height: 1.4;
			color: #666;
			hyphens: none;
			word-break: normal;
		}

		.btn-quote-main {
			padding: 0.9rem 1.5rem;
			margin-top: 0;
			margin-bottom: 0.75rem;
			border-radius: 12px;
			box-shadow: 0 4px 12px rgba(150, 191, 19, 0.3);
			font-size: 1rem;
			font-weight: 700;
			order: -1;
		}

		/* Divider section */
		.quote-divider {
			padding: 0.75rem 0;
			background: transparent;
			box-shadow: none;
			border-radius: 0;
			margin: 0.5rem 0;
			border-top: 1px solid #e5e5e5;
			border-bottom: 1px solid #e5e5e5;
		}

		.quote-divider span {
			font-size: 0.7rem;
			margin: 0;
			display: block;
			text-align: center;
			white-space: nowrap;
			color: #666;
		}

		.phone-input-group {
			margin: 0;
			padding: 0;
			background: transparent;
			border-radius: 0;
		}

		.quote-footer {
			padding: 0;
			background: transparent;
			margin-top: 0.5rem;
			border-radius: 0;
			box-shadow: none;
			text-align: center;
		}

		.quote-footer .call-label {
			font-size: 0.7rem;
			margin-bottom: 0.3rem;
		}

		.quote-footer .phone-number {
			font-size: 1.1rem;
			font-weight: 700;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 0.5rem;
			margin-bottom: 0.3rem;
		}

		.quote-footer .disclaimer-text {
			font-size: 0.65rem;
			margin: 0;
			opacity: 0.7;
		}

		.phone-input {
			padding: 0.6rem 0.5rem;
			font-size: 0.85rem;
			text-align: center;
			min-width: 0;
			margin-bottom: 0;
		}

		.btn {
			width: 100%;
			padding: 0.75rem 1rem;
			font-size: 0.9rem;
			min-height: 44px;
			margin-bottom: 0.5rem;
		}

		.btn-submit {
			padding: 0.75rem 1rem;
			font-size: 0.9rem;
		}

		.quote-card hr {
			margin: 0.5rem 0;
		}

		.quote-card .phone-link {
			font-size: 1rem;
			margin-top: 0.4rem;
			display: block;
		}

		/* Reduce text spacing */
		.quote-card .call-us-text {
			font-size: 0.7rem;
			margin-bottom: 0.3rem;
		}

		.quote-card strong {
			font-size: 0.8rem;
		}

		.stats-grid {
			grid-template-columns: 1fr;
			gap: 2rem;
		}

		.stat-item {
			padding: 1.5rem;
		}

		.stat-value {
			font-size: 3.5rem !important;
			font-weight: 900 !important;
		}

		.stat-item:last-child .stat-value {
			font-size: 2rem !important;
			font-weight: 900 !important;
		}

		.stat-card .stat-label {
			font-size: 5.1rem !important;
			font-weight: 600 !important;
			line-height: 1.5 !important;
		}

		.stat-card {
			padding: 1.5rem;
		}

		.cta-buttons {
			flex-direction: column;
			gap: 1rem;
		}

		.phone-input-group {
			flex-direction: row;
		}

		.btn-submit {
			flex-shrink: 0;
			padding: 0.6rem 1rem;
			font-size: 0.8rem;
		}

		.question-content {
			height: 500px;
			padding: 1.25rem;
			border-radius: 20px;
		}

		.options-grid,
		.options-grid.grid-4 {
			grid-template-columns: repeat(2, 1fr);
			gap: 0.625rem;
		}

		.option-card {
			min-height: 105px;
			padding: 0.75rem 0.875rem;
			flex-direction: column;
			align-items: center;
			border-radius: 10px;
		}

		.option-icon {
			font-size: 2.25rem;
			margin-bottom: 0.4rem;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.option-icon :global(svg) {
			width: 2.25rem;
			height: 2.25rem;
			stroke: #1a1a1a;
		}

		.option-content h3 {
			font-size: 0.925rem;
			margin-bottom: 0.15rem;
			font-weight: 700;
			line-height: 1.3;
		}

		.option-content p {
			font-size: 0.7rem;
			line-height: 1.3;
			opacity: 0.8;
		}

		.progress-bar {
			background: rgba(0, 0, 0, 0.1);
			margin-bottom: 1.5rem;
		}

		.message-content {
			background: rgba(0, 0, 0, 0.03);
			border: 2px solid rgba(150, 191, 19, 0.3);
			padding: 1.25rem 1rem;
			margin-bottom: 1rem;
		}

		.message-text {
			color: #1a1a1a;
			font-size: 0.9rem;
			line-height: 1.5;
		}

		.message-screen {
			padding: 1rem 0;
		}

		/* Progress message badge - more compact */
		.progress-message-badge {
			padding: 0.5rem 1rem !important;
			margin: 0 auto 1rem !important;
			gap: 0.5rem !important;
		}

		.message-badge-icon {
			width: 20px !important;
			height: 20px !important;
			font-size: 0.85rem !important;
		}

		.progress-message-badge span {
			font-size: 0.85rem !important;
			line-height: 1.2 !important;
		}

		/* Question title more compact */
		.question-header {
			margin-bottom: 1rem !important;
		}

		.form-group label {
			color: #1a1a1a;
			font-size: 0.95rem;
		}

		.form-input {
			background: rgba(0, 0, 0, 0.03);
			border: 2px solid rgba(0, 0, 0, 0.15);
			color: #1a1a1a;
			padding: 1.2rem 1rem;
			font-size: 1rem;
		}

		.form-input::placeholder {
			color: rgba(0, 0, 0, 0.4);
		}

		.form-input:focus {
			background: rgba(0, 0, 0, 0.05);
			border-color: #96bf13;
		}

		.form-sublabel {
			color: rgba(0, 0, 0, 0.6);
			font-size: 0.9rem;
		}

		.privacy-notice {
			color: rgba(0, 0, 0, 0.5);
			font-size: 0.8rem;
		}

		.form-label {
			font-size: 1.05rem;
			line-height: 1.25;
			color: #1a1a1a;
			margin-bottom: 0.5rem;
		}

		.question-title {
			font-size: 1.1rem;
			line-height: 1.25;
			margin-bottom: 1rem;
		}

		.logo-img {
			height: 40px;
		}

		.footer-logo-img {
			height: 60px;
		}

		/* Improve touch targets for mobile */
		button, a {
			min-height: 44px;
		}

		/* Fix text overflow */
		h1, h2, h3, h4, h5, h6 {
			word-wrap: break-word;
			overflow-wrap: break-word;
		}

		/* Comparison Table Mobile Redesign */
		.comparison {
			padding: 3rem 0 2rem;
		}

		.comparison .section-header h2 {
			font-size: 1.5rem;
			margin-bottom: 0.5rem;
		}

		.comparison-table {
			margin: 1.5rem 0 0;
			border-radius: 0;
			overflow: visible;
			box-shadow: none;
			background: transparent;
		}

		.comparison-header {
			display: grid;
			grid-template-columns: 1.5fr 0.75fr 0.75fr;
			gap: 0.4rem;
			padding: 0 0.6rem;
			background: transparent;
			border-radius: 0;
			margin-bottom: 0.75rem;
		}

		.header-feature {
			display: block;
			font-size: 0.7rem;
			font-weight: 600;
			color: #666;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			align-self: center;
		}

		.header-company {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 0.3rem;
			background: white;
			border-radius: 8px;
			padding: 0.75rem 0.5rem;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
			min-height: 70px;
		}

		.panther-header {
			background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
		}

		.panther-header span {
			color: #96bf13 !important;
		}

		.competitors-header {
			background: white;
			border: 1px solid #e0e0e0;
		}

		.competitors-header span {
			color: #1a1a1a !important;
		}

		.header-company span {
			font-size: 0.7rem;
			line-height: 1.3;
			font-weight: 600;
			text-align: center;
		}

		.comparison-logo {
			height: 22px;
			margin-bottom: 0.25rem;
		}

		.comparison-body {
			background: transparent;
		}

		.comparison-row {
			display: grid !important;
			grid-template-columns: 1.5fr 0.75fr 0.75fr !important;
			grid-template-areas: "name panther competitor" !important;
			gap: 0.4rem !important;
			padding: 0.75rem 0.6rem !important;
			margin-bottom: 0.5rem !important;
			background: white !important;
			border-radius: 8px !important;
			align-items: center !important;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
		}

		.comparison-row:hover {
			background: white !important;
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
		}

		.comparison-row.even {
			background: white !important;
		}

		.feature-name {
			grid-area: name !important;
			font-size: 0.75rem !important;
			margin-bottom: 0 !important;
			padding: 0 !important;
			font-weight: 500 !important;
			text-align: left !important;
			background: transparent !important;
			border-radius: 0 !important;
			line-height: 1.3 !important;
			color: #1a1a1a !important;
		}

		.panther-value {
			grid-area: panther !important;
			background: rgba(150, 191, 19, 0.1) !important;
			border: none !important;
		}

		.competitors-value {
			grid-area: competitor !important;
			background: #f8f8f8 !important;
		}

		.feature-value {
			display: flex !important;
			align-items: center !important;
			justify-content: center !important;
			margin: 0 !important;
			padding: 0.5rem !important;
			border-radius: 6px !important;
			box-shadow: none !important;
			min-height: 40px !important;
		}

		.feature-value svg {
			width: 20px !important;
			height: 20px !important;
		}

		.comparison-footer {
			padding: 1.5rem 0.5rem;
			background: transparent;
		}

		.btn-comparison-cta {
			width: 100%;
			padding: 0.9rem 1.5rem;
			font-size: 0.95rem;
			border-radius: 8px;
		}

		.btn-comparison-cta .arrow {
			font-size: 1.2rem;
		}

		/* Slider arrows - make them much smaller on mobile */
		.slider-arrow,
		.slider-arrow-left,
		.slider-arrow-right {
			width: 24px !important;
			height: 24px !important;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15) !important;
			min-width: 24px !important;
			min-height: 24px !important;
			max-width: 24px !important;
			max-height: 24px !important;
		}

		.slider-arrow svg,
		.slider-arrow-left svg,
		.slider-arrow-right svg {
			width: 12px !important;
			height: 12px !important;
		}

		.slider-arrow:hover,
		.slider-arrow-left:hover,
		.slider-arrow-right:hover {
			transform: scale(1.05) !important;
		}

		/* Slider dots - balanced size */
		.placa-slider-dots {
			margin-top: 0.5rem !important;
			gap: 5px !important;
		}

		.slider-dot {
			width: 6px !important;
			height: 6px !important;
			min-width: 6px !important;
			min-height: 6px !important;
		}

		.slider-dot.active {
			width: 16px !important;
			height: 6px !important;
			min-width: 16px !important;
			min-height: 6px !important;
		}

		/* Testimonials Mobile - One Card at a Time */
		.testimonials {
			padding: 2rem 0 !important;
		}

		.testimonials .section-header {
			margin-bottom: 1.25rem !important;
		}

		.testimonials .section-header h2 {
			font-size: 1.4rem !important;
			margin-bottom: 0.4rem !important;
			line-height: 1.25 !important;
		}

		.testimonials .section-header p {
			font-size: 0.8rem !important;
			margin: 0 !important;
			line-height: 1.35 !important;
			color: #666 !important;
		}

		.testimonials-container {
			position: relative;
			margin-top: 1.25rem;
			padding: 0;
		}

		.testimonials-grid {
			display: block !important;
			width: 100%;
			overflow: visible;
			touch-action: pan-y;
			user-select: none;
		}

		.testimonial-card {
			width: 100%;
			padding: 1.5rem 1rem !important;
			margin: 0;
			box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
			gap: 1rem !important;
		}

		.testimonial-card:hover {
			transform: none;
		}

		.testimonial-arrow {
			display: none !important;
		}

		.testimonial-dots {
			margin-top: 0.5rem !important;
			gap: 5px !important;
		}

		.dot {
			width: 6px !important;
			height: 6px !important;
			min-width: 6px !important;
			min-height: 6px !important;
		}

		.dot.active {
			width: 16px !important;
			height: 6px !important;
			min-width: 16px !important;
			min-height: 6px !important;
		}

		.quote-icon {
			width: 32px !important;
			height: 32px !important;
		}

		.testimonial-text {
			font-size: 0.95rem !important;
			line-height: 1.6 !important;
		}

		.testimonial-rating {
			gap: 0.2rem !important;
		}

		.star {
			font-size: 1.1rem !important;
		}

		.testimonial-author {
			padding-top: 0.75rem !important;
			gap: 0.75rem !important;
		}

		.author-avatar {
			width: 42px !important;
			height: 42px !important;
		}

		.author-name {
			font-size: 0.95rem !important;
			margin-bottom: 0.2rem !important;
		}

		.author-meta {
			font-size: 0.8rem !important;
		}
	}

	/* Extra Small Devices (very small phones, less than 360px) */
	@media (max-width: 360px) {
		.options-grid,
		.options-grid.grid-4 {
			grid-template-columns: repeat(2, 1fr);
			gap: 0.5rem;
		}

		.option-card {
			min-height: 95px;
			padding: 0.5rem 0.625rem;
			flex-direction: column;
			align-items: center;
			border-radius: 10px;
		}

		.option-icon {
			font-size: 1.75rem;
			margin-bottom: 0.25rem;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.option-icon :global(svg) {
			width: 1.75rem;
			height: 1.75rem;
			stroke: #1a1a1a;
		}

		.option-content h3 {
			font-size: 0.8rem;
			margin-bottom: 0.05rem;
			line-height: 1.2;
		}

		.option-content p {
			font-size: 0.6rem;
			line-height: 1.2;
		}

		.question-title {
			font-size: 1.1rem;
			line-height: 1.25;
			margin-bottom: 1rem;
		}

		.question-content {
			height: 450px;
			padding: 1rem;
			border-radius: 18px;
		}

		.form-label {
			font-size: 1rem;
			color: #1a1a1a;
		}
	}

	/* Loading spinner styles */
	.loading-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background: rgba(0, 0, 0, 0.6);
		backdrop-filter: blur(4px);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10001;
		animation: fadeIn 0.2s ease;
	}

	.loading-modal {
		background: white;
		padding: 3rem 4rem;
		border-radius: 16px;
		text-align: center;
		box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
		animation: slideUp 0.3s ease;
		max-width: 90%;
	}

	.spinner {
		width: 60px;
		height: 60px;
		border: 4px solid #f3f3f3;
		border-top: 4px solid #1e40af;
		border-radius: 50%;
		animation: spin 1s linear infinite;
		margin: 0 auto 1.5rem;
	}

	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}

	.loading-message {
		font-size: 1.25rem;
		font-weight: 600;
		color: #1f2937;
		margin: 0 0 0.5rem;
	}

	.loading-submessage {
		font-size: 0.95rem;
		color: #6b7280;
		margin: 0;
	}
</style>